import { useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useAuth } from '../../../AuthContext';
import useAuditLog, { AUDIT_ACTION } from '../../../hooks/useAuditLog';
import { fetchData, postData, updateData, deleteData, activateData } from '../../../apiService';
import apiConfig from '../../../apiConfig';
import LogoLoader from '../../common/LogoLoader';
import { useSessionValidation } from '../../../utils/sessionValidation';
import {
  Add, Edit, Visibility, Delete, Payment, FilterList, Refresh, Warning, CheckCircle, Cancel, Close, Print, LocalShipping
} from '@mui/icons-material';
import {
  Button, IconButton, Tooltip, Chip, Alert, Snackbar,
  Dialog, DialogTitle, DialogContent, DialogActions, Typography, Box, Divider, Grid,
  FormControl, InputLabel, Select, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TextField, InputAdornment,
  useMediaQuery
} from '@mui/material';
import { Search } from '@mui/icons-material';
import PaymentReceiptsModal from './PaymentReceiptsModal';
import './PaymentReceiptsIndex.css';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import MampillyLogo from '../Logo/logo_Madavana.png';


const PaymentReceiptsIndex = () => {
  // Session validation
  useSessionValidation();
  
  const history = useHistory();
  const location = useLocation();
  const { companyId, branchId, token, userId, permissions } = useAuth();
  const { logAudit } = useAuditLog();

  const hasCreatePermission = permissions?.includes('Create Master');
  const hasEditPermission = permissions?.includes('Edit Master');
  const hasDeletePermission = permissions?.includes('Delete Master');

  // Modal states
  const [openModal, setOpenModal] = useState(false);
  const [modalAction, setModalAction] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);

  // Data states
  const [paymentReceipts, setPaymentReceipts] = useState([]);
  const [masters, setMasters] = useState([]);
  const [banks, setBanks] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [sales, setSales] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [gstRates, setGstRates] = useState([]);
  const [companyData, setCompanyData] = useState(null);
  const [branchData, setBranchData] = useState(null);

  // Search and filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all'); // payment or receipt
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const isMobile = useMediaQuery('(max-width:768px)');
  const [showSearch, setShowSearch] = useState(false);

  const formatDateToYYYYMMDD = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  const getDefaultFromDate = () => {
    const today = new Date();
    return formatDateToYYYYMMDD(new Date(today.getFullYear(), today.getMonth(), 1));
  };
  const getCurrentDate = () => formatDateToYYYYMMDD(new Date());

  // Today's date - used as the maximum selectable date so the year cannot go
  // beyond the current year (prevents future dates and the "0001" year glitch).
  const todayStr = getCurrentDate();
  // Minimum selectable date - prevents the native date spinner from rolling the
  // year down to values like 0001 when the up/down arrows are used.
  const minDateStr = '2000-01-01';

  const [fromDate, setFromDate] = useState(getDefaultFromDate());
  const [toDate, setToDate] = useState(getCurrentDate());

  // Date range validation handlers
  const [dateError, setDateError] = useState('');

  const handleFromDateChange = (value) => {
    if (!value) return;
    if (value < minDateStr) {
      setDateError('Invalid date. Please select a valid date (year must be 2000 or later).');
      setFromDate(value);
      setPaymentReceipts([]);
      return;
    }
    if (value > todayStr) {
      setDateError('From Date cannot be a future date. Please select today or an earlier date.');
      setFromDate(value);
      setPaymentReceipts([]);
      return;
    }
    if (toDate && value > toDate) {
      setDateError('From Date should not be greater than To Date.');
      setFromDate(value);
      setPaymentReceipts([]);
      return;
    }
    setDateError('');
    setFromDate(value);
    setCurrentPage(1);
  };

  const handleToDateChange = (value) => {
    if (!value) return;
    if (value < minDateStr) {
      setDateError('Invalid date. Please select a valid date (year must be 2000 or later).');
      setToDate(value);
      setPaymentReceipts([]);
      return;
    }
    if (value > todayStr) {
      setDateError('To Date cannot be a future date. Please select today or an earlier date.');
      setToDate(value);
      setPaymentReceipts([]);
      return;
    }
    if (fromDate && fromDate > value) {
      setDateError('From Date should not be greater than To Date.');
      setToDate(value);
      setPaymentReceipts([]);
      return;
    }
    setDateError('');
    setToDate(value);
    setCurrentPage(1);
  };

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Form states
  const [formData, setFormData] = useState({});
  const [paymentReceiptDetails, setPaymentReceiptDetails] = useState([]);

  // Loading and error states
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Loading...');
  const [modalLoading, setModalLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Delete/Activate confirmation modals
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [deleteItemNo, setDeleteItemNo] = useState('');
  const [activateModalOpen, setActivateModalOpen] = useState(false);
  const [activateItemId, setActivateItemId] = useState(null);
  const [activateItemNo, setActivateItemNo] = useState('');

  // Cheque status update modal
  const [chequeStatusModalOpen, setChequeStatusModalOpen] = useState(false);
  const [selectedChequeItem, setSelectedChequeItem] = useState(null);
  const [newChequeStatus, setNewChequeStatus] = useState(1);
  const [bulkChequeStatusModalOpen, setBulkChequeStatusModalOpen] = useState(false);
  const [selectedChequeItems, setSelectedChequeItems] = useState([]);
  const [individualChequeStatuses, setIndividualChequeStatuses] = useState({}); // Store individual status for each item

  // COD Receipts modal
  const [codModalOpen, setCodModalOpen] = useState(false);
  const [codList, setCodList] = useState([]);
  const [codLoading, setCodLoading] = useState(false);
  const [codSearch, setCodSearch] = useState('');

  // Drill-down states for animated expansion - REMOVED (not needed)

  useEffect(() => {
    if (companyId && token && fromDate && toDate && !dateError) {
      fetchAllData();
    }
  }, [companyId, token, fromDate, toDate, dateError]);

  // Auto-open create modal when navigated from Home with openCreate flag
  useEffect(() => {
    if (location.state?.openCreate) {
      const { masterId, masterName, amount } = location.state;
      history.replace('/PaymentAndReceiptIndex', {});

      const openWithData = async () => {
        const maxBillData = await fetchMaxBillNo(true);
        const billNumber = maxBillData.billPrefix && maxBillData.finYear
          ? `${maxBillData.billPrefix}/${maxBillData.billNumber}/${maxBillData.finYear}`
          : maxBillData.billNumber.toString();

        setModalAction('create');
        setFormData({
          Id: 0,
          CompanyId: companyId,
          BranchId: branchId,
          BillNumber: billNumber,
          BillDate: new Date().toISOString().split('T')[0],
          MasterId: masterId || '',
          SubHeadId: '',
          PartyId: '',
          IsType: true,
          IsGST: false,
          IsIGST: false,
          GSTHeadId: '',
          IsSaleAndPurchase: false,
          PaymentType: 1,
          ChequeDate: '',
          ChequeNumber: '',
          BankId: null,
          chequeDetails: '',
          ChequeStatusId: 1,
          Amount: amount || 0,
          GSTRate: 0,
          CGSTAmount: 0,
          SGSTAmount: 0,
          IGSTAmount: 0,
          RoundOff: 0,
          TotalAmount: amount || 0,
          Remarks: '',
          IsActive: true,
          MasterName: masterName || '',
        });
        setPaymentReceiptDetails([]);
        setOpenModal(true);

        if (masterId) {
          fetchBillsForMaster(masterId, true);
        }
      };

      openWithData();
    }
  }, [location.state]);

  // Fetch functions
  const fetchPaymentReceipts = async () => {
    try {
      const activeResponse = await fetchData('PaymentAndReceiptApi', `/ActiveList/${companyId}/${branchId}/${fromDate}/${toDate}`, token);
      const activeData = (activeResponse.response_data || activeResponse || []).map(item => ({ ...item, IsActive: item.IsActive ?? true }));
      
      let inactiveData = [];
      try {
        const inactiveResponse = await fetchData('PaymentAndReceiptApi', `/InActiveList/${companyId}/${branchId}/${fromDate}/${toDate}`, token);
        inactiveData = (inactiveResponse.response_data || inactiveResponse || []).map(item => ({ ...item, IsActive: false }));
      } catch (inactiveError) {
        console.error('Error fetching inactive payment receipts:', inactiveError);
      }
      
      setPaymentReceipts([...activeData, ...inactiveData]);
    } catch (error) {
      console.error('Error fetching payment receipts:', error);
      setPaymentReceipts([]);
    }
  };

  const fetchMasters = async () => {
    try {
      const response = await fetchData('PaymentAndReceiptApi', `/Master/${companyId}`, token);
      setMasters(response.response_data || response || []);
    } catch (error) {
      console.error('Error fetching masters:', error);
      setMasters([]);
    }
  };

  const fetchBanks = async () => {
    try {
      const response = await fetchData('BankApi', `/${companyId}`, token);
      setBanks(response.response_data || response || []);
    } catch (error) {
      console.error('Error fetching banks:', error);
      setBanks([]);
    }
  };

  const fetchPurchases = async (masterId = null) => {
    if (!masterId || masterId === 0) {
      setPurchases([]);
      return;
    }
    
    try {
      console.log('Fetching purchases for masterId:', masterId);
      const endpoint = `/GetAllPendingPurchase/${companyId}/${masterId}`;
      const response = await fetchData('PaymentAndReceiptApi', endpoint, token);
      const purchaseData = response.response_data || response || [];
      console.log('Fetched purchases:', purchaseData.length, 'items');
      setPurchases(purchaseData);
    } catch (error) {
      console.error('Error fetching purchases:', error);
      setPurchases([]);
    }
  };

  const fetchSales = async (masterId = null) => {
    if (!masterId || masterId === 0) {
      setSales([]);
      return;
    }
    
    try {
      console.log('Fetching sales for masterId:', masterId);
      const endpoint = `/GetAllPendingSales/${companyId}/${masterId}`;
      const response = await fetchData('PaymentAndReceiptApi', endpoint, token);
      const salesData = response.response_data || response || [];
      console.log('Fetched sales:', salesData.length, 'items');
      setSales(salesData);
    } catch (error) {
      console.error('Error fetching sales:', error);
      setSales([]);
    }
  };

  // Fetch bills based on transaction type (Payment or Receipt)
  const fetchBillsForMaster = async (masterId, isType) => {
    if (!masterId || masterId === 0) {
      setPurchases([]);
      setSales([]);
      return;
    }

    // Clear both immediately so stale data from previous master is gone
    setPurchases([]);
    setSales([]);

    // If Payment (isType = true), fetch Purchase bills
    // If Receipt (isType = false), fetch Sales bills
    if (isType) {
      await fetchPurchases(masterId);
    } else {
      await fetchSales(masterId);
    }
  };

  const fetchPaymentMethods = async () => {
    try {
      const response = await fetchData('PaymentAndReceiptApi', `/PaymentModes/${companyId}`, token);
      const paymentMethodsData = response.response_data || response || [];
      
      // Debug logging to check API response
      console.log('=== PAYMENT METHODS API RESPONSE ===');
      console.log('Raw response:', response);
      console.log('Payment methods data:', paymentMethodsData);
      console.log('Payment methods structure:');
      paymentMethodsData.forEach((method, index) => {
        console.log(`Method ${index + 1}:`, {
          Id: method.Id,
          Name: method.Name,
          IsActive: method.IsActive,
          FullObject: method
        });
      });
      console.log('=== END PAYMENT METHODS DEBUG ===');
      
      setPaymentMethods(paymentMethodsData);
    } catch (error) {
      console.error('Error fetching payment methods:', error);
      
      // Fallback to default payment methods if API fails
      const fallbackMethods = [
        { Id: 1, Name: 'Cash', IsActive: true },
        { Id: 2, Name: 'Cheque', IsActive: true },
        { Id: 3, Name: 'Online Transfer', IsActive: true }
      ];
      
      console.log('=== USING FALLBACK PAYMENT METHODS ===');
      console.log('Fallback methods:', fallbackMethods);
      console.log('=== END FALLBACK DEBUG ===');
      
      setPaymentMethods(fallbackMethods);
    }
  };

  const fetchGstRates = async () => {
    try {
      const response = await fetchData('PaymentAndReceiptApi', `/GetGSTList/${companyId}`, token);
      setGstRates(response.response_data || response || []);
    } catch (error) {
      console.error('Error fetching GST rates:', error);
      // Fallback to default GST rates if API fails
      setGstRates([
        { Id: 1, Rate: 0, Name: '0%', IsActive: true },
        { Id: 2, Rate: 5, Name: '5%', IsActive: true },
        { Id: 3, Rate: 12, Name: '12%', IsActive: true },
        { Id: 4, Rate: 18, Name: '18%', IsActive: true },
        { Id: 5, Rate: 28, Name: '28%', IsActive: true }
      ]);
    }
  };

  const fetchCompanyData = async () => {
    try {
      const response = await fetchData('CompanyApi', `/CompanyDetails/${companyId}`, token);
      setCompanyData(response.response_data || response || null);
    } catch (error) {
      console.error('Error fetching company data:', error);
      setCompanyData(null);
    }
  };

  const fetchBranchData = async () => {
    try {
      const response = await fetchData('BranchApi', `/BranchDetails/${branchId}`, token);
      const data = response.response_data || response || null;
      setBranchData(data);
    } catch (error) {
      console.error('Error fetching branch data:', error);
      setBranchData(null);
    }
  };

  const fetchMaxBillNo = async (isType = true) => {
    try {
      const isTypeString = isType.toString();
      const response = await fetchData('PaymentAndReceiptApi', `/MaxBillno/${branchId || 1}/${companyId}/${isTypeString}`, token);
      const data = response.response_data || response || {};
      const billNumber = data.BillNumber || data.billNumber || 1;
      const billPrefix = data.BillPrefix || data.billPrefix || '';
      const finYear = data.FinYear || data.finYear || '';
      return { billNumber, billPrefix, finYear };
    } catch (error) {
      console.error('Error fetching max bill number:', error);
      return { billNumber: 1, billPrefix: '', finYear: '' };
    }
  };

  const fetchAllData = async () => {
    setLoading(true);
    setLoadingMessage('Fetching payment receipts...');
    try {
      await Promise.allSettled([
        fetchPaymentReceipts(),
        fetchMasters(),
        fetchBanks(),
        fetchPurchases(),
        fetchSales(),
        fetchPaymentMethods(),
        fetchGstRates(),
        fetchCompanyData(),
        fetchBranchData()
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch specific payment receipt details by ID
  const fetchPaymentReceiptDetails = async (paymentReceiptId) => {
    try {
      const response = await fetchData('PaymentAndReceiptApi', `/PaymentData/${paymentReceiptId}`, token);
      return response.response_data || response || null;
    } catch (error) {
      console.error('Error fetching payment receipt details:', error);
      // Try alternative endpoint if GetById doesn't work
      try {
        const altResponse = await fetchData('PaymentAndReceiptApi', `/${paymentReceiptId}`, token);
        return altResponse.response_data || altResponse || null;
      } catch (altError) {
        console.error('Error with alternative endpoint:', altError);
        return null;
      }
    }
  };

  // Print Payment/Receipt handler - Professional jsPDF Format
  const handlePrintPaymentReceipt = async (paymentReceiptId) => {
    logAudit(AUDIT_ACTION.PRINT, 'PaymentReceiptApi', paymentReceiptId);
    setLoading(true);
    setLoadingMessage('Generating PDF...');
    
    try {
      const freshData = await fetchPaymentReceiptDetails(paymentReceiptId);
      
      if (!freshData) {
        setError('Failed to fetch payment receipt details for printing.');
        setLoading(false);
        return;
      }
      
      const paymentData = freshData.PaymentAndReceipt || freshData;
      const purchaseDetails = freshData.PaymentAndReceiptPurchase || [];
      const saleDetails = freshData.PaymentAndReceiptSale || [];

      // Get master details
      const master = masters.find(m => m.Id === paymentData.MasterId);
      const masterName = paymentData.MasterName || master?.Name || '-';
      const masterAddress = master?.Address || '';
      const masterGST = master?.GSTNumber || '';
      const masterContact = master?.ContactNumber || master?.Mobile || '';

      // Get bank details
      const paymentBank = banks.find(b => b.Id === paymentData.BankId);
      const bankName = paymentBank?.Name || paymentBank?.BankName || '';
      const bankBranch = paymentBank?.BranchName || '';
      const bankAccount = paymentBank?.AccountNumber || paymentBank?.AccountNo || '';
      const bankIFSC = paymentBank?.IFSCCode || paymentBank?.IFSC || '';
      const bankContact = paymentBank?.ContactNumber || paymentBank?.ContactNo || '';

      // Get payment method
      const paymentMethod = paymentMethods.find(pm => pm.Id === paymentData.PaymentType);
      const paymentMethodName = paymentMethod?.Name || 'Cash';

      // Company / branch details (mirrors Sale PDF)
      const coName = branchData?.BranchName || branchData?.Name || companyData?.Name || 'Company Name';
      const coBranchName = branchData?.BranchName || '';
      const coGST = branchData?.GSTNumber || companyData?.GSTNumber || '';
      const coState = branchData?.StateName || companyData?.StateName || '';
      const coStateCode = branchData?.StateCode || companyData?.StateCode || '';
      const coAddr = [branchData?.AddressLine1, branchData?.AddressLine2, branchData?.Location, branchData?.PinCode]
        .filter(Boolean).join(', ') || companyData?.Address || '';
      const coTel1 = branchData?.ContactNumber1 || companyData?.ContactNumber || '';
      const coTel2 = branchData?.ContactNumber2 || '';
      const coPhones = [coTel1, coTel2].filter(Boolean).join(', ');

      const isPayment = paymentData.IsType;
      const documentType = isPayment ? 'PAYMENT VOUCHER' : 'RECEIPT VOUCHER';
      const partyLabel = isPayment ? 'Paid To' : 'Received From';
      const totalAmount = parseFloat(paymentData.TotalAmount || paymentData.Amount) || 0;

      // ── pdf setup (matches Sale PDF exactly) ─────────────────────────────────
      const pdf = new jsPDF('p', 'mm', 'a4');
      const PW = pdf.internal.pageSize.width;   // 210
      const PH = pdf.internal.pageSize.height/2;  // 297
      const M = 5;
      const CW = PW - 2 * M;
      const black = [0, 0, 0];
      const white = [255, 255, 255];

      const drawText = (text, x, y, opts = {}) => {
        const { fs = 8, fw = 'normal', col = black, align = 'left', mw = null } = opts;
        pdf.setFontSize(fs);
        pdf.setFont('helvetica', fw);
        pdf.setTextColor(...col);
        const str = String(text ?? '');
        if (mw) {
          const lines = pdf.splitTextToSize(str, mw);
          pdf.text(lines, x, y, { align });
          return y + lines.length * fs * 0.38;
        }
        pdf.text(str, x, y, { align });
        return y + fs * 0.38;
      };

      const hLine = (x1, y1, x2, y2, lw = 0.3) => {
        pdf.setDrawColor(0, 0, 0);
        pdf.setLineWidth(lw);
        pdf.line(x1, y1, x2, y2);
      };

      const drawRect = (x, y, w, h, lw = 0.3) => {
        pdf.setDrawColor(0, 0, 0);
        pdf.setLineWidth(lw);
        pdf.rect(x, y, w, h, 'S');
      };

      let y = M;

      // ── OUTER BORDER ──────────────────────────────────────────────────────────
      drawRect(M, M, CW, PH - 2 * M, 0.5);

      // ── HEADER ────────────────────────────────────────────────────────────────
      const logoSize = 14;
      const rightColX = M + CW * 0.68;
      const leftTextX = M + logoSize + 6;
      const leftMaxW = rightColX - leftTextX - 4;
      const rightValX = rightColX + 22;
      const rightMaxW = M + CW - rightValX - 2;

      pdf.setFontSize(7);
      const addrLines = pdf.splitTextToSize(coAddr, leftMaxW);
      const headerH = Math.max(26, 14 + addrLines.length * 3.5 + 8);

      try {
        pdf.addImage(MampillyLogo, 'PNG', M + 3, y + 3, logoSize, logoSize);
      } catch (logoErr) {
        console.warn('Logo load failed, skipping', logoErr);
      }

      drawText(coName.toUpperCase(), leftTextX, y + 7, { fs: 12, fw: 'bold' });
      let headerTextY = y + 12;
      pdf.setFontSize(7);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(...black);
      pdf.text(addrLines, leftTextX, headerTextY);
      headerTextY += addrLines.length * 3.5;
      drawText(`GST NO : ${coGST}  State : ${coState} - ${coStateCode}`, leftTextX, headerTextY, { fs: 7 });
      headerTextY += 4;
      if (coPhones) drawText(coPhones, leftTextX, headerTextY, { fs: 7 });

      // Right side: Voucher No, Date, FY, Mode
      hLine(rightColX, y, rightColX, y + headerH);
      let ry = y + 5;
      drawText('Voucher No', rightColX + 2, ry, { fs: 7, fw: 'bold' });
      drawText(`${paymentData.BillPrefix ? paymentData.BillPrefix + '/' : ''}${paymentData.BillNumber || ''}`, rightValX, ry, { fs: 7, mw: rightMaxW });
      ry += 5;
      drawText('Date', rightColX + 2, ry, { fs: 7, fw: 'bold' });
      drawText(formatDate(paymentData.BillDate), rightValX, ry, { fs: 7, mw: rightMaxW });
      ry += 5;
      drawText('Fin Year', rightColX + 2, ry, { fs: 7, fw: 'bold' });
      drawText(String(paymentData.FinYear || ''), rightValX, ry, { fs: 7, mw: rightMaxW });
      ry += 5;
      drawText('Mode', rightColX + 2, ry, { fs: 7, fw: 'bold' });
      drawText(paymentMethodName, rightValX, ry, { fs: 6.5, mw: rightMaxW });

      y += headerH;
      hLine(M, y, M + CW, y);

      // ── DOCUMENT TYPE TITLE ───────────────────────────────────────────────────
      drawText(documentType, M + CW / 2, y + 9, { fs: 9, fw: 'bold', align: 'center' });
      y += 13;
      hLine(M, y, M + CW, y);

      // ── PARTY SECTION ─────────────────────────────────────────────────────────
      const midX = M + CW * 0.5;
      const partyRowH = 22;

      drawText(partyLabel, M + 2, y + 4, { fs: 7, fw: 'bold' });
      drawText(masterName, M + 2, y + 8, { fs: 8, fw: 'bold', mw: midX - M - 5 });
      let partyY = y + 12;
      if (masterAddress) {
        drawText(masterAddress, M + 2, partyY, { fs: 7, col: [60, 60, 60], mw: midX - M - 5 });
        partyY += 4;
      }
      if (masterContact) {
        drawText(masterContact, M + 2, partyY, { fs: 7, col: [30, 64, 175] });
      }

      hLine(midX, y, midX, y + partyRowH);
      drawText('GST No :', midX + 2, y + 4, { fs: 7, fw: 'bold' });
      drawText(masterGST, midX + 22, y + 4, { fs: 7 });
      if (paymentData.ChequeNumber) {
        drawText('Cheque No :', midX + 2, y + 8, { fs: 7, fw: 'bold' });
        drawText(paymentData.ChequeNumber, midX + 22, y + 8, { fs: 7 });
      }

      y += partyRowH;
      hLine(M, y, M + CW, y);

      // ── TRANSACTION TABLE ─────────────────────────────────────────────────────
      const tableBody = [];
      if (isPayment && purchaseDetails.length > 0) {
        purchaseDetails.forEach((d, idx) => {
          tableBody.push([idx + 1, 'Purchase Bill Payment', `Bill No: ${d.BillNumber || d.PurchaseId || '-'}`, Number(d.Amount || 0).toFixed(2)]);
        });
      } else if (!isPayment && saleDetails.length > 0) {
        saleDetails.forEach((d, idx) => {
          tableBody.push([idx + 1, 'Sale Bill Receipt', `Bill No: ${d.BillNumber || d.SaleId || '-'}`, Number(d.Amount || 0).toFixed(2)]);
        });
      } else {
        tableBody.push([1, isPayment ? 'General Payment' : 'General Receipt', paymentData.Remarks || '-', totalAmount.toFixed(2)]);
      }

      autoTable(pdf, {
        startY: y,
        head: [['No', 'Particulars', 'Description / Bill No', 'Amount']],
        body: tableBody,
        theme: 'grid',
        headStyles: {
          fillColor: white, textColor: black, fontSize: 7, fontStyle: 'bold',
          halign: 'center', cellPadding: 1.5, lineColor: black, lineWidth: 0.3,
        },
        bodyStyles: {
          fontSize: 7, textColor: black, cellPadding: 1.5, lineColor: black, lineWidth: 0.2,
        },
        columnStyles: {
          0: { halign: 'center', cellWidth: 10 },
          1: { halign: 'left', cellWidth: 50 },
          2: { halign: 'left' },
          3: { halign: 'right', cellWidth: 28 },
        },
        margin: { left: M, right: M },
        tableLineColor: black,
        tableLineWidth: 0.3,
      });

      y = pdf.lastAutoTable?.finalY || y + 20;
      hLine(M, y, M + CW, y);
      
      // ── BOTTOM SECTION — Bank details (left) | Totals summary (right) ─────────
      const bottomY = y;
      const rightSummaryX = M + CW * 0.55;

      // Left: Bank details
      let ly = bottomY + 5;
      if (bankName) {
        drawText('Bank', M + 3, ly, { fs: 7, fw: 'bold' });
        drawText(`: ${bankName}${bankBranch ? ', ' + bankBranch : ''}`, M + 24, ly, { fs: 7 });
        ly += 4.5;
        drawText('Account No', M + 3, ly, { fs: 7, fw: 'bold' });
        drawText(`: ${bankAccount}`, M + 24, ly, { fs: 7 });
        ly += 4.5;
        drawText('IFSC', M + 3, ly, { fs: 7, fw: 'bold' });
        drawText(`: ${bankIFSC}`, M + 24, ly, { fs: 7 });
        ly += 4.5;
        drawText('GPay / Contact', M + 3, ly, { fs: 7, fw: 'bold' });
        drawText(`: ${bankContact || coPhones || ''}`, M + 24, ly, { fs: 7 });
        ly += 4.5;
      } else {
        drawText('Mode', M + 3, ly, { fs: 7, fw: 'bold' });
        drawText(`: ${paymentMethodName}`, M + 24, ly, { fs: 7 });
        ly += 4.5;
        if (coPhones) {
          drawText('Contact', M + 3, ly, { fs: 7, fw: 'bold' });
          drawText(`: ${coPhones}`, M + 24, ly, { fs: 7 });
          ly += 4.5;
        }
      }
      if (paymentData.ChequeNumber) {
        drawText('Cheque No', M + 3, ly, { fs: 7, fw: 'bold' });
        drawText(`: ${paymentData.ChequeNumber}`, M + 24, ly, { fs: 7 });
        ly += 4.5;
      }

      // Right: Amount summary
      let sy = bottomY + 5;
      const labelX = rightSummaryX + 3;
      const valX = M + CW - 3;
      const summaryLine = (label, value) => {
        drawText(label, labelX, sy, { fs: 7, fw: 'bold' });
        drawText(value, valX, sy, { fs: 7, align: 'right' });
        sy += 5;
      };

      summaryLine('Basic Amount', Number(paymentData.Amount || 0).toFixed(2));
      if ((paymentData.CGSTAmount || 0) > 0) summaryLine('CGST', Number(paymentData.CGSTAmount).toFixed(2));
      if ((paymentData.SGSTAmount || 0) > 0) summaryLine('SGST', Number(paymentData.SGSTAmount).toFixed(2));
      if ((paymentData.IGSTAmount || 0) > 0) summaryLine('IGST', Number(paymentData.IGSTAmount).toFixed(2));
      if (paymentData.RoundOff) summaryLine('Round Off', Number(paymentData.RoundOff).toFixed(2));

      hLine(rightSummaryX, sy, M + CW, sy);
      sy += 1;
      drawText('Total Amount', labelX, sy + 4, { fs: 9, fw: 'bold' });
      drawText(totalAmount.toFixed(2), valX, sy + 4, { fs: 9, fw: 'bold', align: 'right' });

      const bottomSectionEndY = Math.max(ly + 2, sy + 8);
      hLine(rightSummaryX, bottomY, rightSummaryX, bottomSectionEndY);
      hLine(M, bottomSectionEndY, M + CW, bottomSectionEndY);

      // ── AMOUNT IN WORDS ───────────────────────────────────────────────────────
      const wordsY = bottomSectionEndY + 5;
      drawText(
        `Rupees ${numberToWords(Math.floor(totalAmount))} and ${Math.round((totalAmount % 1) * 100)} Paise Only`,
        M + 3, wordsY, { fs: 7, fw: 'bold', mw: rightSummaryX - M - 5 }
      );

      // ── FOOTER — Remarks (left) | Company signature (right) ──────────────────
      const footerLineY = wordsY + 6;
      hLine(M, footerLineY, M + CW, footerLineY);

      const sigLineY = footerLineY + 22;
      hLine(rightSummaryX, footerLineY, rightSummaryX, sigLineY);

      if (paymentData.Remarks) {
        drawText(paymentData.Remarks, M + 3, footerLineY + 5, { fs: 7, mw: rightSummaryX - M - 8 });
      }

      drawText(`For  ${coName.toUpperCase()}`, rightSummaryX + 3, footerLineY + 5, { fs: 8, fw: 'bold' });
      drawText('Authorised Signatory', rightSummaryX + 3, footerLineY + 18, { fs: 8, fw: 'bold' });

      hLine(M, sigLineY, M + CW, sigLineY, 0.5);

      // ── PRINT ─────────────────────────────────────────────────────────────────
      pdf.autoPrint();
      const pdfBlob = pdf.output('blob');
      const pdfUrl = URL.createObjectURL(pdfBlob);
      const printWindow = window.open(pdfUrl, '_blank');
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print();
          setTimeout(() => URL.revokeObjectURL(pdfUrl), 1000);
        };
      } else {
        const link = document.createElement('a');
        link.href = pdfUrl;
        link.download = `${documentType.replace(' ', '_')}_${paymentData.BillNumber}_${formatDate(paymentData.BillDate).replace(/\//g, '-')}.pdf`;
        link.click();
        URL.revokeObjectURL(pdfUrl);
      }

      setSuccess(`Document prepared for printing: ${documentType} ${paymentData.BillNumber}`);
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      setError('Failed to generate PDF.');
    }
    
    setLoading(false);
  };

  // Send PDF via WhatsApp
  const handleSendWhatsApp = async (paymentReceiptId) => {
    setLoading(true);
    setLoadingMessage('Preparing PDF for WhatsApp...');

    try {
      const freshData = await fetchPaymentReceiptDetails(paymentReceiptId);

      if (!freshData) {
        setError('Failed to fetch payment receipt details.');
        setLoading(false);
        return;
      }

      const paymentData = freshData.PaymentAndReceipt || freshData;
      const purchaseDetails = freshData.PaymentAndReceiptPurchase || [];
      const saleDetails = freshData.PaymentAndReceiptSale || [];

      // Master & bank details
      const master = masters.find(m => m.Id === paymentData.MasterId);
      const masterName = paymentData.MasterName || master?.Name || '-';
      const masterAddress = master?.Address || '';
      const masterGST = master?.GSTNumber || '';
      const masterContact = master?.ContactNumber || master?.Mobile || '';

      const paymentBank = banks.find(b => b.Id === paymentData.BankId);
      const bankName = paymentBank?.Name || paymentBank?.BankName || '';
      const bankBranch = paymentBank?.BranchName || '';
      const bankAccount = paymentBank?.AccountNumber || paymentBank?.AccountNo || '';
      const bankIFSC = paymentBank?.IFSCCode || paymentBank?.IFSC || '';
      const bankContact = paymentBank?.ContactNumber || paymentBank?.ContactNo || '';

      const paymentMethod = paymentMethods.find(pm => pm.Id === paymentData.PaymentType);
      const paymentMethodName = paymentMethod?.Name || 'Cash';

      const coName = branchData?.BranchName || branchData?.Name || companyData?.Name || 'Company Name';
      const coGST = branchData?.GSTNumber || companyData?.GSTNumber || '';
      const coState = branchData?.StateName || companyData?.StateName || '';
      const coStateCode = branchData?.StateCode || companyData?.StateCode || '';
      const coAddr = [branchData?.AddressLine1, branchData?.AddressLine2, branchData?.Location, branchData?.PinCode]
        .filter(Boolean).join(', ') || companyData?.Address || '';
      const coTel1 = branchData?.ContactNumber1 || companyData?.ContactNumber || '';
      const coTel2 = branchData?.ContactNumber2 || '';
      const coPhones = [coTel1, coTel2].filter(Boolean).join(', ');

      const isPayment = paymentData.IsType;
      const documentType = isPayment ? 'PAYMENT VOUCHER' : 'RECEIPT VOUCHER';
      const partyLabel = isPayment ? 'Paid To' : 'Received From';
      const totalAmount = parseFloat(paymentData.TotalAmount || paymentData.Amount) || 0;

      // ── Build the same PDF as print ──────────────────────────────────────────
      const pdf = new jsPDF('p', 'mm', 'a4');
      const PW = pdf.internal.pageSize.width;
      const PH = pdf.internal.pageSize.height / 2;
      const M = 5;
      const CW = PW - 2 * M;
      const black = [0, 0, 0];
      const white = [255, 255, 255];

      const drawText = (text, x, y, opts = {}) => {
        const { fs = 8, fw = 'normal', col = black, align = 'left', mw = null } = opts;
        pdf.setFontSize(fs);
        pdf.setFont('helvetica', fw);
        pdf.setTextColor(...col);
        const str = String(text ?? '');
        if (mw) {
          const lines = pdf.splitTextToSize(str, mw);
          pdf.text(lines, x, y, { align });
          return y + lines.length * fs * 0.38;
        }
        pdf.text(str, x, y, { align });
        return y + fs * 0.38;
      };

      const hLine = (x1, y1, x2, y2, lw = 0.3) => {
        pdf.setDrawColor(0, 0, 0);
        pdf.setLineWidth(lw);
        pdf.line(x1, y1, x2, y2);
      };

      const drawRect = (x, y, w, h, lw = 0.3) => {
        pdf.setDrawColor(0, 0, 0);
        pdf.setLineWidth(lw);
        pdf.rect(x, y, w, h, 'S');
      };

      let y = M;
      drawRect(M, M, CW, PH - 2 * M, 0.5);

      const logoSize = 14;
      const rightColX = M + CW * 0.68;
      const leftTextX = M + logoSize + 6;
      const leftMaxW = rightColX - leftTextX - 4;
      const rightValX = rightColX + 22;
      const rightMaxW = M + CW - rightValX - 2;

      pdf.setFontSize(7);
      const addrLines = pdf.splitTextToSize(coAddr, leftMaxW);
      const headerH = Math.max(26, 14 + addrLines.length * 3.5 + 8);

      try {
        pdf.addImage(MampillyLogo, 'PNG', M + 3, y + 3, logoSize, logoSize);
      } catch (logoErr) {
        console.warn('Logo load failed', logoErr);
      }

      drawText(coName.toUpperCase(), leftTextX, y + 7, { fs: 12, fw: 'bold' });
      let headerTextY = y + 12;
      pdf.setFontSize(7);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(...black);
      pdf.text(addrLines, leftTextX, headerTextY);
      headerTextY += addrLines.length * 3.5;
      drawText(`GST NO : ${coGST}  State : ${coState} - ${coStateCode}`, leftTextX, headerTextY, { fs: 7 });
      headerTextY += 4;
      if (coPhones) drawText(coPhones, leftTextX, headerTextY, { fs: 7 });

      hLine(rightColX, y, rightColX, y + headerH);
      let ry = y + 5;
      drawText('Voucher No', rightColX + 2, ry, { fs: 7, fw: 'bold' });
      drawText(`${paymentData.BillPrefix ? paymentData.BillPrefix + '/' : ''}${paymentData.BillNumber || ''}`, rightValX, ry, { fs: 7, mw: rightMaxW });
      ry += 5;
      drawText('Date', rightColX + 2, ry, { fs: 7, fw: 'bold' });
      drawText(formatDate(paymentData.BillDate), rightValX, ry, { fs: 7, mw: rightMaxW });
      ry += 5;
      drawText('Fin Year', rightColX + 2, ry, { fs: 7, fw: 'bold' });
      drawText(String(paymentData.FinYear || ''), rightValX, ry, { fs: 7, mw: rightMaxW });
      ry += 5;
      drawText('Mode', rightColX + 2, ry, { fs: 7, fw: 'bold' });
      drawText(paymentMethodName, rightValX, ry, { fs: 6.5, mw: rightMaxW });

      y += headerH;
      hLine(M, y, M + CW, y);

      drawText(documentType, M + CW / 2, y + 9, { fs: 9, fw: 'bold', align: 'center' });
      y += 13;
      hLine(M, y, M + CW, y);

      const midX = M + CW * 0.5;
      const partyRowH = 22;

      drawText(partyLabel, M + 2, y + 4, { fs: 7, fw: 'bold' });
      drawText(masterName, M + 2, y + 8, { fs: 8, fw: 'bold', mw: midX - M - 5 });
      let partyY = y + 12;
      if (masterAddress) { drawText(masterAddress, M + 2, partyY, { fs: 7, col: [60, 60, 60], mw: midX - M - 5 }); partyY += 4; }
      if (masterContact) drawText(masterContact, M + 2, partyY, { fs: 7, col: [30, 64, 175] });

      hLine(midX, y, midX, y + partyRowH);
      drawText('GST No :', midX + 2, y + 4, { fs: 7, fw: 'bold' });
      drawText(masterGST, midX + 22, y + 4, { fs: 7 });
      if (paymentData.ChequeNumber) {
        drawText('Cheque No :', midX + 2, y + 8, { fs: 7, fw: 'bold' });
        drawText(paymentData.ChequeNumber, midX + 22, y + 8, { fs: 7 });
      }

      y += partyRowH;
      hLine(M, y, M + CW, y);

      const tableBody = [];
      if (isPayment && purchaseDetails.length > 0) {
        purchaseDetails.forEach((d, idx) => {
          tableBody.push([idx + 1, 'Purchase Bill Payment', `Bill No: ${d.BillNumber || d.PurchaseId || '-'}`, Number(d.Amount || 0).toFixed(2)]);
        });
      } else if (!isPayment && saleDetails.length > 0) {
        saleDetails.forEach((d, idx) => {
          tableBody.push([idx + 1, 'Sale Bill Receipt', `Bill No: ${d.BillNumber || d.SaleId || '-'}`, Number(d.Amount || 0).toFixed(2)]);
        });
      } else {
        tableBody.push([1, isPayment ? 'General Payment' : 'General Receipt', paymentData.Remarks || '-', totalAmount.toFixed(2)]);
      }

      autoTable(pdf, {
        startY: y,
        head: [['No', 'Particulars', 'Description / Bill No', 'Amount']],
        body: tableBody,
        theme: 'grid',
        headStyles: { fillColor: white, textColor: black, fontSize: 7, fontStyle: 'bold', halign: 'center', cellPadding: 1.5, lineColor: black, lineWidth: 0.3 },
        bodyStyles: { fontSize: 7, textColor: black, cellPadding: 1.5, lineColor: black, lineWidth: 0.2 },
        columnStyles: { 0: { halign: 'center', cellWidth: 10 }, 1: { halign: 'left', cellWidth: 50 }, 2: { halign: 'left' }, 3: { halign: 'right', cellWidth: 28 } },
        margin: { left: M, right: M },
        tableLineColor: black,
        tableLineWidth: 0.3,
      });

      y = pdf.lastAutoTable?.finalY || y + 20;
      hLine(M, y, M + CW, y);

      const bottomY = y;
      const rightSummaryX = M + CW * 0.55;

      let ly = bottomY + 5;
      if (bankName) {
        drawText('Bank', M + 3, ly, { fs: 7, fw: 'bold' });
        drawText(`: ${bankName}${bankBranch ? ', ' + bankBranch : ''}`, M + 24, ly, { fs: 7 });
        ly += 4.5;
        drawText('Account No', M + 3, ly, { fs: 7, fw: 'bold' });
        drawText(`: ${bankAccount}`, M + 24, ly, { fs: 7 });
        ly += 4.5;
        drawText('IFSC', M + 3, ly, { fs: 7, fw: 'bold' });
        drawText(`: ${bankIFSC}`, M + 24, ly, { fs: 7 });
        ly += 4.5;
        drawText('GPay / Contact', M + 3, ly, { fs: 7, fw: 'bold' });
        drawText(`: ${bankContact || coPhones || ''}`, M + 24, ly, { fs: 7 });
        ly += 4.5;
      } else {
        drawText('Mode', M + 3, ly, { fs: 7, fw: 'bold' });
        drawText(`: ${paymentMethodName}`, M + 24, ly, { fs: 7 });
        ly += 4.5;
        if (coPhones) { drawText('Contact', M + 3, ly, { fs: 7, fw: 'bold' }); drawText(`: ${coPhones}`, M + 24, ly, { fs: 7 }); ly += 4.5; }
      }
      if (paymentData.ChequeNumber) {
        drawText('Cheque No', M + 3, ly, { fs: 7, fw: 'bold' });
        drawText(`: ${paymentData.ChequeNumber}`, M + 24, ly, { fs: 7 });
        ly += 4.5;
      }

      let sy = bottomY + 5;
      const labelX = rightSummaryX + 3;
      const valX = M + CW - 3;
      const summaryLine = (label, value) => {
        drawText(label, labelX, sy, { fs: 7, fw: 'bold' });
        drawText(value, valX, sy, { fs: 7, align: 'right' });
        sy += 5;
      };
      summaryLine('Basic Amount', Number(paymentData.Amount || 0).toFixed(2));
      if ((paymentData.CGSTAmount || 0) > 0) summaryLine('CGST', Number(paymentData.CGSTAmount).toFixed(2));
      if ((paymentData.SGSTAmount || 0) > 0) summaryLine('SGST', Number(paymentData.SGSTAmount).toFixed(2));
      if ((paymentData.IGSTAmount || 0) > 0) summaryLine('IGST', Number(paymentData.IGSTAmount).toFixed(2));
      if (paymentData.RoundOff) summaryLine('Round Off', Number(paymentData.RoundOff).toFixed(2));

      hLine(rightSummaryX, sy, M + CW, sy);
      sy += 1;
      drawText('Total Amount', labelX, sy + 4, { fs: 9, fw: 'bold' });
      drawText(totalAmount.toFixed(2), valX, sy + 4, { fs: 9, fw: 'bold', align: 'right' });

      const bottomSectionEndY = Math.max(ly + 2, sy + 8);
      hLine(rightSummaryX, bottomY, rightSummaryX, bottomSectionEndY);
      hLine(M, bottomSectionEndY, M + CW, bottomSectionEndY);

      const wordsY = bottomSectionEndY + 5;
      drawText(
        `Rupees ${numberToWords(Math.floor(totalAmount))} and ${Math.round((totalAmount % 1) * 100)} Paise Only`,
        M + 3, wordsY, { fs: 7, fw: 'bold', mw: rightSummaryX - M - 5 }
      );

      const footerLineY = wordsY + 6;
      hLine(M, footerLineY, M + CW, footerLineY);

      const sigLineY = footerLineY + 22;
      hLine(rightSummaryX, footerLineY, rightSummaryX, sigLineY);

      if (paymentData.Remarks) drawText(paymentData.Remarks, M + 3, footerLineY + 5, { fs: 7, mw: rightSummaryX - M - 8 });
      drawText(`For  ${coName.toUpperCase()}`, rightSummaryX + 3, footerLineY + 5, { fs: 8, fw: 'bold' });
      drawText('Authorised Signatory', rightSummaryX + 3, footerLineY + 18, { fs: 8, fw: 'bold' });
      hLine(M, sigLineY, M + CW, sigLineY, 0.5);

      // ── Share via WhatsApp ──────────────────────────────────────────────────
      const fileName = `${documentType.replace(' ', '_')}_${paymentData.BillNumber}_${formatDate(paymentData.BillDate).replace(/\//g, '-')}.pdf`;

      // Try Web Share API first (mobile-friendly)
      if (navigator.canShare) {
        const pdfBlob = pdf.output('blob');
        const pdfFile = new File([pdfBlob], fileName, { type: 'application/pdf' });
        if (navigator.canShare({ files: [pdfFile] })) {
          try {
            await navigator.share({ files: [pdfFile], title: documentType, text: `${documentType} - ${masterName} - ₹${totalAmount.toFixed(2)}` });
            setSuccess('PDF shared successfully!');
            setLoading(false);
            return;
          } catch (shareErr) {
            if (shareErr.name !== 'AbortError') {
              console.warn('Web Share failed, falling back to download+WhatsApp link', shareErr);
            } else {
              setLoading(false);
              return; // User cancelled — do nothing
            }
          }
        }
      }

      // Fallback: download the PDF and open WhatsApp web with a pre-filled message
      const pdfBlob = pdf.output('blob');
      const pdfUrl = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = fileName;
      link.click();
      setTimeout(() => URL.revokeObjectURL(pdfUrl), 2000);

      const whatsappText = encodeURIComponent(
        `${documentType}\nVoucher No: ${paymentData.BillPrefix ? paymentData.BillPrefix + '/' : ''}${paymentData.BillNumber}\nDate: ${formatDate(paymentData.BillDate)}\nParty: ${masterName}\nAmount: ₹${totalAmount.toFixed(2)}\n\nPlease find the attached PDF.`
      );
      window.open(`https://wa.me/?text=${whatsappText}`, '_blank');

      setSuccess('PDF downloaded. Please attach it in the WhatsApp window that opened.');
    } catch (err) {
      console.error('Error sending WhatsApp:', err);
      setError('Failed to prepare PDF for WhatsApp.');
    }

    setLoading(false);
  };

  // Helper function to convert number to words (basic implementation)
  const numberToWords = (num) => {
    if (num === 0) return 'Zero';
    
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
    const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    
    const convertHundreds = (n) => {
      let result = '';
      if (n >= 100) {
        result += ones[Math.floor(n / 100)] + ' Hundred ';
        n %= 100;
      }
      if (n >= 20) {
        result += tens[Math.floor(n / 10)] + ' ';
        n %= 10;
      } else if (n >= 10) {
        result += teens[n - 10] + ' ';
        return result;
      }
      if (n > 0) {
        result += ones[n] + ' ';
      }
      return result;
    };
    
    let result = '';
    const crores = Math.floor(num / 10000000);
    if (crores > 0) {
      result += convertHundreds(crores) + 'Crore ';
      num %= 10000000;
    }
    
    const lakhs = Math.floor(num / 100000);
    if (lakhs > 0) {
      result += convertHundreds(lakhs) + 'Lakh ';
      num %= 100000;
    }
    
    const thousands = Math.floor(num / 1000);
    if (thousands > 0) {
      result += convertHundreds(thousands) + 'Thousand ';
      num %= 1000;
    }
    
    if (num > 0) {
      result += convertHundreds(num);
    }
    
    return result.trim();
  };

  // Handle modal actions
  const handleOpenModal = async (action, item = null) => {
    setModalAction(action);
    if (action === 'view') {
      const recordId = item?.Id || 0;
      logAudit(AUDIT_ACTION.VIEW, 'PaymentReceiptApi', recordId);
    }
    setSelectedItem(item);
    
    if (action === 'create') {
      const defaultIsType = true; // Default to Payment, can be changed in modal
      const maxBillData = await fetchMaxBillNo(defaultIsType);
      const billNumber = maxBillData.billPrefix && maxBillData.finYear ? 
        `${maxBillData.billPrefix}/${maxBillData.billNumber}/${maxBillData.finYear}` : 
        maxBillData.billNumber.toString();
      
      setFormData({
        Id: 0,
        CompanyId: companyId,
        BranchId: branchId,
        BillNumber: billNumber,
        BillDate: new Date().toISOString().split('T')[0],
        MasterId: '',
        SubHeadId: '',
        PartyId: '',
        IsType: true, // true for Payment, false for Receipt
        IsGST: false,
        IsIGST: false, // Add IGST field
        GSTHeadId: '',
        IsSaleAndPurchase: false,
        PaymentType: 1, // 1=Cash, 2=Cheque, 3=Online
        ChequeDate: '',
        ChequeNumber: '',
        BankId: null,
        chequeDetails: '',
        ChequeStatusId: 1,
        Amount: 0,
        GSTRate: 0,
        CGSTAmount: 0,
        SGSTAmount: 0,
        IGSTAmount: 0,
        RoundOff: 0,
        TotalAmount: 0,
        Remarks: '',
        IsActive: true
      });
      setPaymentReceiptDetails([]);
    } else if (action === 'edit' || action === 'view') {
      setModalLoading(true);
      try {
        // Fetch complete payment receipt data from API - following JournalEntry pattern
        console.log('Fetching payment receipt data for ID:', item.Id);
        const response = await fetchData('PaymentAndReceiptApi', `/PaymentData/${item.Id}`, token);
        const paymentReceiptData = response.response_data || response;
        console.log('Fetched payment receipt data:', paymentReceiptData);
        
        const paymentData = paymentReceiptData.PaymentAndReceipt;
        
        setFormData({
          ...paymentData,
          BillDate: paymentData.BillDate ? paymentData.BillDate.split('T')[0] : new Date().toISOString().split('T')[0],
          ChequeDate: paymentData.ChequeDate ? paymentData.ChequeDate.split('T')[0] : '',
          MasterId: paymentData.MasterId || '',
          BankId: paymentData.BankId || null
        });
        
        // Set payment receipt details from the API response
        const purchaseDetails = paymentReceiptData.PaymentAndReceiptPurchase || [];
        const saleDetails = paymentReceiptData.PaymentAndReceiptSale || [];

        const masterId = paymentData.MasterId;
        const isPayment = paymentData.IsType;

        // For edit/view mode: fetch ALL pending bills for this master and merge with saved bills.
        // Saved bills (already fully paid) may not appear in the pending list, so we always
        // include them to ensure the user sees the complete bill history for this voucher.
        try {
          if (isPayment) {
            const pendingRes = await fetchData('PaymentAndReceiptApi', `/GetAllPendingPurchase/${companyId}/${masterId}`, token);
            const pendingList = pendingRes.response_data || pendingRes || [];
            // Build a map of saved PaymentAmounts by PurchaseId
            const savedMap = {};
            purchaseDetails.forEach(p => { savedMap[p.PurchaseId] = p; });

            // Helper to build a description for a purchase bill — use Billdetails from API directly
            const buildPurchaseDesc = (p) => {
              return p.Billdetails || p.billdetails || p.BillDetails || p.PurchaseDescription || p.purchaseDescription || p.Description || `Purchase - ${p.PurchaseId}`;
            };

            // Start with pending bills, marking ones that were saved in this voucher
            const pendingIds = new Set(pendingList.map(p => p.PurchaseId));
            const mergedDetails = pendingList.map(p => ({
              Id: p.PurchaseId,
              Description: buildPurchaseDesc(p),
              Amount: parseFloat(p.PurchaseAmount || 0),
              PaidAmount: parseFloat(p.PaidAmount || 0),
              PendingAmount: parseFloat(p.PendingAmount || 0),
              BillDate: p.BillDate,
              Type: 'Purchase',
              PaymentAmount: savedMap[p.PurchaseId] ? parseFloat(savedMap[p.PurchaseId].Amount || 0) : 0,
              IsSelected: !!savedMap[p.PurchaseId]
            }));

            // Add saved bills that are NOT in the pending list (fully paid bills)
            purchaseDetails.forEach(p => {
              if (!pendingIds.has(p.PurchaseId)) {
                mergedDetails.unshift({
                  Id: p.PurchaseId,
                  Description: buildPurchaseDesc(p),
                  Amount: parseFloat(p.PurchaseAmount || p.BillAmount || 0),
                  PaidAmount: parseFloat(p.PaidAmount || 0),
                  PendingAmount: parseFloat(p.PendingAmount || 0),
                  BillDate: p.BillDate || paymentData.BillDate,
                  Type: 'Purchase',
                  PaymentAmount: parseFloat(p.Amount || 0),
                  IsSelected: true
                });
              }
            });

            setPaymentReceiptDetails(mergedDetails.length > 0 ? mergedDetails : [{ noData: true }]);
          } else {
            const pendingRes = await fetchData('PaymentAndReceiptApi', `/GetAllPendingSales/${companyId}/${masterId}`, token);
            const pendingList = pendingRes.response_data || pendingRes || [];
            const savedMap = {};
            saleDetails.forEach(s => { savedMap[s.SaleId] = s; });

            const pendingIds = new Set(pendingList.map(s => s.SaleId));
            const mergedDetails = pendingList.map(s => ({
              Id: s.SaleId,
              Description: s.SaleDescription || `Sale - ${s.SaleId}`,
              Amount: parseFloat(s.SaleAmount || 0),
              PaidAmount: parseFloat(s.PaidAmount || 0),
              PendingAmount: parseFloat(s.PendingAmount || 0),
              BillDate: s.BillDate,
              Type: 'Sale',
              PaymentAmount: savedMap[s.SaleId] ? parseFloat(savedMap[s.SaleId].Amount || 0) : 0,
              IsSelected: !!savedMap[s.SaleId]
            }));

            // Add saved bills that are NOT in the pending list (fully paid bills)
            saleDetails.forEach(s => {
              if (!pendingIds.has(s.SaleId)) {
                mergedDetails.unshift({
                  Id: s.SaleId,
                  Description: s.SaleDescription || s.Description || `Sale - ${s.SaleId}`,
                  Amount: parseFloat(s.SaleAmount || s.BillAmount || 0),
                  PaidAmount: parseFloat(s.PaidAmount || 0),
                  PendingAmount: parseFloat(s.PendingAmount || 0),
                  BillDate: s.BillDate || paymentData.BillDate,
                  Type: 'Sale',
                  PaymentAmount: parseFloat(s.Amount || 0),
                  IsSelected: true
                });
              }
            });

            setPaymentReceiptDetails(mergedDetails.length > 0 ? mergedDetails : [{ noData: true }]);
          }
        } catch (billFetchError) {
          console.error('Error fetching pending bills for edit mode:', billFetchError);
          // Fallback: show only the saved linked bills
          if (isPayment && purchaseDetails.length > 0) {
            setPaymentReceiptDetails(purchaseDetails.map(p => {
              const rawDate = p.BillDate || p.billDate || paymentData.BillDate || '';
              const desc    = p.Billdetails || p.billdetails || p.BillDetails || p.PurchaseDescription || p.purchaseDescription || p.Description || `Purchase - ${p.PurchaseId}`;
              return {
                Id: p.PurchaseId,
                Description: desc,
                Amount: parseFloat(p.PurchaseAmount || p.BillAmount || 0),
                PaidAmount: parseFloat(p.PaidAmount || 0),
                PendingAmount: parseFloat(p.PendingAmount || 0),
                BillDate: rawDate,
                Type: 'Purchase',
                PaymentAmount: parseFloat(p.Amount || 0),
                IsSelected: true
              };
            }));
          } else if (!isPayment && saleDetails.length > 0) {
            setPaymentReceiptDetails(saleDetails.map(s => ({
              Id: s.SaleId,
              Description: s.SaleDescription || s.Description || `Sale - ${s.SaleId}`,
              Amount: parseFloat(s.SaleAmount || s.BillAmount || 0),
              PaidAmount: parseFloat(s.PaidAmount || 0),
              PendingAmount: parseFloat(s.PendingAmount || 0),
              BillDate: s.BillDate || paymentData.BillDate,
              Type: 'Sale',
              PaymentAmount: parseFloat(s.Amount || 0),
              IsSelected: true
            })));
          } else {
            setPaymentReceiptDetails([{ noData: true }]);
          }
        }
        
      } catch (error) {
        console.error('Error fetching payment receipt details:', error);
        setError('Failed to fetch payment receipt details.');
        // Fallback to using the item data from the list
        setFormData({
          ...item,
          BillDate: item.BillDate ? item.BillDate.split('T')[0] : new Date().toISOString().split('T')[0],
          ChequeDate: item.ChequeDate ? item.ChequeDate.split('T')[0] : '',
          MasterId: item.MasterId || ''
        });
        
        // No linked bills available in fallback
        setPaymentReceiptDetails([{ noData: true }]);
      }
      setModalLoading(false);
    }
    
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedItem(null);
    setFormData({});
    setPaymentReceiptDetails([]);
  };

  // Handle save
  const handleSave = async () => {
    setModalLoading(true);
    try {
      const dataToSend = {
        PaymentAndReceipt: {
          Id: modalAction === 'edit' ? formData.Id : 0,
          CompanyId: companyId,
          BranchId: branchId,
          MasterId: parseInt(formData.MasterId),
          SubHeadId: formData.SubHeadId || 0,
          PartyId: formData.PartyId || formData.MasterId, // Use MasterId as PartyId if PartyId not set
          BillDate: formData.BillDate,
          BillNumber: parseInt(formData.BillNumber) || 0,
          FinYear: formData.FinYear || new Date().getFullYear().toString(),
          IsType: formData.IsType, // true for Payment, false for Receipt
          IsGST: formData.IsGST || false,
          GSTHeadId: formData.GSTHeadId || 0,
          IsSaleAndPurchase: formData.IsSaleAndPurchase || true,
          PaymentType: formData.PaymentType || 1, // 1=Cash, 2=Cheque, etc.
          ChequeDate: formData.ChequeDate || null,
          ChequeNumber: formData.ChequeNumber || null,
          BankId: formData.BankId || null,
          chequeDetails: formData.chequeDetails || null,
          ChequeStatusId: formData.ChequeStatusId || 1,
          Amount: parseFloat(formData.Amount) || 0,
          GSTRate: parseFloat(formData.GSTRate) || 0,
          CGSTAmount: parseFloat(formData.CGSTAmount) || 0,
          SGSTAmount: parseFloat(formData.SGSTAmount) || 0,
          IGSTAmount: parseFloat(formData.IGSTAmount) || 0,
          RoundOff: parseFloat(formData.RoundOff) || 0,
          TotalAmount: parseFloat(formData.TotalAmount) || parseFloat(formData.Amount) || 0,
          Remarks: formData.Remarks || null,
          IsActive: true,
          CreatedBy: modalAction === 'create' ? userId : formData.CreatedBy,
          UpdatedBy: modalAction === 'edit' ? userId : undefined,
          CreatedDate: modalAction === 'create' ? new Date().toISOString() : formData.CreatedDate,
          UpdatedDate: modalAction === 'edit' ? new Date().toISOString() : undefined
        }
      };

      // Add purchase/sale details if any are selected
      const selectedDetails = paymentReceiptDetails.filter(d => d.IsSelected && d.PaymentAmount > 0);
      if (selectedDetails.length > 0) {
        if (formData.IsType) {
          // Payment - add purchase details
          dataToSend.PaymentAndReceiptPurchase = selectedDetails.map(d => ({
            Id: 0,
            CompanyId: companyId,
            BranchId: branchId,
            MasterId: parseInt(formData.MasterId),
            PurchaseId: d.Id,
            PaymentAndReceiptId: 0, // Will be set by backend after PaymentAndReceipt is created
            Amount: parseFloat(d.PaymentAmount) || 0,
            IsActive: true,
            CreatedBy: userId,
            UpdatedBy: null,
            CreatedDate: new Date().toISOString(),
            UpdatedDate: null
          }));
        } else {
          // Receipt - add sale details
          dataToSend.PaymentAndReceiptSale = selectedDetails.map(d => ({
            Id: 0,
            CompanyId: companyId,
            BranchId: branchId,
            MasterId: parseInt(formData.MasterId),
            SaleId: d.Id,
            PaymentAndReceiptId: 0, // Will be set by backend after PaymentAndReceipt is created
            Amount: parseFloat(d.PaymentAmount) || 0,
            IsActive: true,
            CreatedBy: userId,
            UpdatedBy: null,
            CreatedDate: new Date().toISOString(),
            UpdatedDate: null
          }));
        }
      }

      console.log('Saving Payment/Receipt data:', dataToSend);

      if (modalAction === 'create') {
        await postData('PaymentAndReceiptApi', '', dataToSend, token);
        setSuccess(`${formData.IsType ? 'Payment' : 'Receipt'} created successfully!`);
      } else {
        await updateData('PaymentAndReceiptApi', formData.Id, dataToSend, token);
        setSuccess(`${formData.IsType ? 'Payment' : 'Receipt'} updated successfully!`);
      }
      
      handleCloseModal();
      await fetchPaymentReceipts();
    } catch (error) {
      console.error('Error saving payment receipt:', error);
      setError(`Failed to save ${formData.IsType ? 'payment' : 'receipt'}.`);
    }
    setModalLoading(false);
  };

  // Handle delete
  const handleDelete = async () => {
    try {
      await deleteData('PaymentAndReceiptApi', deleteItemId, token);
      setSuccess('Payment/Receipt deleted successfully!');
      logAudit(AUDIT_ACTION.DELETE, 'PaymentAndReceiptApi', deleteItemId, { Id: deleteItemId }, null);
      setDeleteModalOpen(false);
      await fetchPaymentReceipts();
    } catch (error) {
      console.error('Error deleting payment receipt:', error);
      setError('Failed to delete payment receipt.');
    }
  };

  // Handle activate
  const handleActivate = async () => {
    try {
      await activateData('PaymentAndReceiptApi', activateItemId, token);
      setSuccess('Payment/Receipt activated successfully!');
      setActivateModalOpen(false);
      await fetchPaymentReceipts();
    } catch (error) {
      console.error('Error activating payment receipt:', error);
      setError('Failed to activate payment receipt.');
    }
  };

  // Handle cheque status update
  const handleUpdateChequeStatus = async () => {
    try {
      setLoading(true);
      setLoadingMessage('Updating cheque status...');
      
      const updateData = {
        PaymentAndReceipt: {
          Id: selectedChequeItem.Id,
          CompanyId: companyId,
          BranchId: branchId,
          MasterId: selectedChequeItem.MasterId,
          SubHeadId: selectedChequeItem.SubHeadId || 0,
          PartyId: selectedChequeItem.PartyId || selectedChequeItem.MasterId,
          BillDate: selectedChequeItem.BillDate,
          BillNumber: selectedChequeItem.BillNumber,
          FinYear: selectedChequeItem.FinYear || new Date().getFullYear().toString(),
          IsType: selectedChequeItem.IsType,
          IsGST: selectedChequeItem.IsGST || false,
          GSTHeadId: selectedChequeItem.GSTHeadId || 0,
          IsSaleAndPurchase: selectedChequeItem.IsSaleAndPurchase || true,
          PaymentType: selectedChequeItem.PaymentType,
          ChequeDate: selectedChequeItem.ChequeDate,
          ChequeNumber: selectedChequeItem.ChequeNumber,
          BankId: selectedChequeItem.BankId,
          chequeDetails: selectedChequeItem.chequeDetails,
          ChequeStatusId: newChequeStatus, // This is the key update
          Amount: selectedChequeItem.Amount,
          GSTRate: selectedChequeItem.GSTRate || 0,
          CGSTAmount: selectedChequeItem.CGSTAmount || 0,
          SGSTAmount: selectedChequeItem.SGSTAmount || 0,
          IGSTAmount: selectedChequeItem.IGSTAmount || 0,
          RoundOff: selectedChequeItem.RoundOff || 0,
          TotalAmount: selectedChequeItem.TotalAmount,
          Remarks: selectedChequeItem.Remarks,
          IsActive: selectedChequeItem.IsActive,
          CreatedBy: selectedChequeItem.CreatedBy,
          UpdatedBy: userId,
          CreatedDate: selectedChequeItem.CreatedDate,
          UpdatedDate: new Date().toISOString()
        }
      };

      await updateData('PaymentAndReceiptApi', selectedChequeItem.Id, updateData, token);
      
      const statusText = newChequeStatus === 1 ? 'Pending' : 
                        newChequeStatus === 2 ? 'Passed' : 
                        newChequeStatus === 3 ? 'Bounced' : 'Cancelled';
      
      setSuccess(`Cheque status updated to "${statusText}" successfully!`);
      setChequeStatusModalOpen(false);
      setSelectedChequeItem(null);
      await fetchPaymentReceipts();
    } catch (error) {
      console.error('Error updating cheque status:', error);
      setError('Failed to update cheque status.');
    } finally {
      setLoading(false);
    }
  };

  // Handle bulk cheque status update with individual statuses
  const handleBulkUpdateChequeStatus = async () => {
    try {
      setLoading(true);
      setLoadingMessage('Updating cheque statuses...');
      
      const updatePromises = selectedChequeItems.map(async (item) => {
        const individualStatus = individualChequeStatuses[item.Id] || newChequeStatus;
        
        const updatePayload = {
          PaymentAndReceipt: {
            Id: item.Id,
            CompanyId: companyId,
            BranchId: branchId,
            MasterId: item.MasterId,
            SubHeadId: item.SubHeadId || 0,
            PartyId: item.PartyId || item.MasterId,
            BillDate: item.BillDate,
            BillNumber: item.BillNumber,
            FinYear: item.FinYear || new Date().getFullYear().toString(),
            IsType: item.IsType,
            IsGST: item.IsGST || false,
            GSTHeadId: item.GSTHeadId || 0,
            IsSaleAndPurchase: item.IsSaleAndPurchase || true,
            PaymentType: item.PaymentType,
            ChequeDate: item.ChequeDate,
            ChequeNumber: item.ChequeNumber,
            BankId: item.BankId,
            chequeDetails: item.chequeDetails,
            ChequeStatusId: individualStatus, // Use individual status for each item
            Amount: item.Amount,
            GSTRate: item.GSTRate || 0,
            CGSTAmount: item.CGSTAmount || 0,
            SGSTAmount: item.SGSTAmount || 0,
            IGSTAmount: item.IGSTAmount || 0,
            RoundOff: item.RoundOff || 0,
            TotalAmount: item.TotalAmount,
            Remarks: item.Remarks,
            IsActive: item.IsActive,
            CreatedBy: item.CreatedBy,
            UpdatedBy: userId,
            CreatedDate: item.CreatedDate,
            UpdatedDate: new Date().toISOString()
          }
        };

        return updateData('PaymentAndReceiptApi', item.Id, updatePayload, token);
      });

      await Promise.all(updatePromises);
      
      setSuccess(`${selectedChequeItems.length} cheque statuses updated successfully!`);
      setBulkChequeStatusModalOpen(false);
      setSelectedChequeItems([]);
      setIndividualChequeStatuses({});
      await fetchPaymentReceipts();
    } catch (error) {
      console.error('Error updating cheque statuses:', error);
      setError('Failed to update cheque statuses.');
    } finally {
      setLoading(false);
    }
  };

  // Open cheque status update modal
  const handleOpenChequeStatusModal = (item) => {
    setSelectedChequeItem(item);
    setNewChequeStatus(item.ChequeStatusId || 1);
    setChequeStatusModalOpen(true);
  };

  // Get cheque payment method ID dynamically
  const getChequePaymentMethodId = () => {
    console.log('=== GETTING CHEQUE PAYMENT METHOD ID ===');
    console.log('Available payment methods:', paymentMethods);
    
    const chequeMethod = paymentMethods.find(pm => 
      pm.Name && pm.Name.toLowerCase().includes('cheque')
    );
    
    console.log('Found cheque method:', chequeMethod);
    
    const chequeId = chequeMethod ? chequeMethod.Id : 2;
    console.log('Using cheque payment method ID:', chequeId);
    console.log('=== END CHEQUE METHOD DEBUG ===');
    
    return chequeId;
  };

  // Handle individual cheque status update (single item)
  const handleIndividualChequeStatusUpdate = async (item, newStatus) => {
    try {
      setLoading(true);
      setLoadingMessage(`Updating cheque status for ${item.BillNumber}...`);
      
      const updatePayload = {
        PaymentAndReceipt: {
          Id: item.Id,
          CompanyId: companyId,
          BranchId: branchId,
          MasterId: item.MasterId,
          SubHeadId: item.SubHeadId || 0,
          PartyId: item.PartyId || item.MasterId,
          BillDate: item.BillDate,
          BillNumber: item.BillNumber,
          FinYear: item.FinYear || new Date().getFullYear().toString(),
          IsType: item.IsType,
          IsGST: item.IsGST || false,
          GSTHeadId: item.GSTHeadId || 0,
          IsSaleAndPurchase: item.IsSaleAndPurchase || true,
          PaymentType: item.PaymentType,
          ChequeDate: item.ChequeDate,
          ChequeNumber: item.ChequeNumber,
          BankId: item.BankId,
          chequeDetails: item.chequeDetails,
          ChequeStatusId: newStatus,
          Amount: item.Amount,
          GSTRate: item.GSTRate || 0,
          CGSTAmount: item.CGSTAmount || 0,
          SGSTAmount: item.SGSTAmount || 0,
          IGSTAmount: item.IGSTAmount || 0,
          RoundOff: item.RoundOff || 0,
          TotalAmount: item.TotalAmount,
          Remarks: item.Remarks,
          IsActive: item.IsActive,
          CreatedBy: item.CreatedBy,
          UpdatedBy: userId,
          CreatedDate: item.CreatedDate,
          UpdatedDate: new Date().toISOString()
        }
      };

      await updateData('PaymentAndReceiptApi', item.Id, updatePayload, token);
      
      const statusText = newStatus === 1 ? 'Pending' : 
                        newStatus === 2 ? 'Passed' : 
                        newStatus === 3 ? 'Bounced' : 'Cancelled';
      
      setSuccess(`Cheque status updated to "${statusText}" for ${item.BillNumber}!`);
      
      // Update the individual status in state
      setIndividualChequeStatuses(prev => ({
        ...prev,
        [item.Id]: newStatus
      }));
      
      // Refresh the main data
      await fetchPaymentReceipts();
      
    } catch (error) {
      console.error('Error updating individual cheque status:', error);
      setError(`Failed to update cheque status for ${item.BillNumber}.`);
    } finally {
      setLoading(false);
    }
  };

  // Handle individual cheque status change
  const handleIndividualStatusChange = (itemId, newStatus) => {
    setIndividualChequeStatuses(prev => ({
      ...prev,
      [itemId]: newStatus
    }));
  };

  // Get status color and text
  const getStatusInfo = (statusId) => {
    switch (statusId) {
      case 1:
        return { text: 'Pending', color: '#f59e0b', bgColor: '#fef3c7', icon: '⏳' };
      case 2:
        return { text: 'Passed', color: '#10b981', bgColor: '#d1fae5', icon: '✅' };
      case 3:
        return { text: 'Bounced', color: '#ef4444', bgColor: '#fee2e2', icon: '❌' };
      case 4:
        return { text: 'Cancelled', color: '#6b7280', bgColor: '#f3f4f6', icon: '🚫' };
      default:
        return { text: 'Unknown', color: '#6b7280', bgColor: '#f3f4f6', icon: '❓' };
    }
  };

  // Open bulk cheque status update modal
  const handleOpenBulkChequeStatusModal = () => {
    console.log('=== OPENING BULK CHEQUE STATUS MODAL ===');
    
    const chequePaymentMethodId = getChequePaymentMethodId();
    console.log('Filtering items with PaymentType:', chequePaymentMethodId);
    console.log('Total filtered data items:', filteredData.length);
    
    const chequeItems = filteredData.filter(item => {
      const isPaymentTypeMatch = item.PaymentType === chequePaymentMethodId;
      const isActive = item.IsActive;
      const isStatusUpdateable = item.ChequeStatusId === null || item.ChequeStatusId === 0 || item.ChequeStatusId === 1;
      const isMatch = isPaymentTypeMatch && isActive && isStatusUpdateable;
      
      console.log(`Item ${item.Id} (${item.BillNumber}): PaymentType=${item.PaymentType}, IsActive=${item.IsActive}, ChequeStatusId=${item.ChequeStatusId}, Updateable=${isStatusUpdateable}, Match=${isMatch}`);
      return isMatch;
    });
    
    console.log('Found cheque items:', chequeItems.length);
    console.log('Cheque items details:', chequeItems);
    
    if (chequeItems.length === 0) {
      console.log('No updateable cheque items found - showing error');
      setError('No pending cheque payments found to update. Only cheques with Pending status can be updated.');
      return;
    }
    
    setSelectedChequeItems(chequeItems);
    setNewChequeStatus(2); // Default to "Passed"
    
    // Initialize individual statuses with default value
    const initialStatuses = {};
    chequeItems.forEach(item => {
      initialStatuses[item.Id] = item.ChequeStatusId || 1; // Use current status or default to "Pending"
    });
    setIndividualChequeStatuses(initialStatuses);
    
    console.log('Initial statuses:', initialStatuses);
    console.log('=== END MODAL OPENING DEBUG ===');
    
    setBulkChequeStatusModalOpen(true);
  };

  // ── COD Receipts ──────────────────────────────────────────────────────────

  const fetchCODList = async () => {
    setCodLoading(true);
    try {
      const res = await fetchData(
        'PaymentAndReceiptApi',
        `/CODDetails/${companyId}/${companyId}`,
        token
      );
      const list = res?.response_data || res || [];
      setCodList(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error('Error fetching COD list:', error);
      setCodList([]);
    } finally {
      setCodLoading(false);
    }
  };

  const handleOpenCODModal = () => {
    setCodSearch('');
    setCodModalOpen(true);
    fetchCODList();
  };

  // Open Receipt create pre-filled from a COD record
  const handleCODReceiptCreate = async (codItem) => {
    setCodModalOpen(false);

    // Receipts use IsType = false
    const isType = false;
    const maxBillData = await fetchMaxBillNo(isType);
    const billNumber =
      maxBillData.billPrefix && maxBillData.finYear
        ? `${maxBillData.billPrefix}/${maxBillData.billNumber}/${maxBillData.finYear}`
        : maxBillData.billNumber.toString();

    // MasterId comes from CustomerId in the COD record
    const masterId = codItem.CustomerId || null;
    const masterName = codItem.CustomerName || '';
    const codAmount = parseFloat(codItem.ToCollect || 0);

    setModalAction('create');
    setSelectedItem(null);
    setFormData({
      Id: 0,
      CompanyId: companyId,
      BranchId: branchId,
      BillNumber: billNumber,
      BillDate: new Date().toISOString().split('T')[0],
      MasterId: masterId || '',
      MasterName: masterName,
      SubHeadId: '',
      PartyId: '',
      IsType: isType,           // Receipt mode
      IsGST: false,
      IsIGST: false,
      GSTHeadId: '',
      IsSaleAndPurchase: !!masterId,
      PaymentType: 1,           // Cash default (COD is cash)
      ChequeDate: '',
      ChequeNumber: '',
      BankId: null,
      chequeDetails: '',
      ChequeStatusId: 1,
      Amount: codAmount,
      GSTRate: 0,
      CGSTAmount: 0,
      SGSTAmount: 0,
      IGSTAmount: 0,
      RoundOff: 0,
      TotalAmount: codAmount,
      Remarks: `COD - Bill: ${codItem.BillNo || ''}`,
      IsActive: true,
      // Store the SaleId so the modal can pre-select it
      _codSaleId: codItem.SaleId || null,
    });
    setPaymentReceiptDetails([]);
    setOpenModal(true);

    // Fetch sale bills for this customer; the modal will auto-select the matching SaleId
    if (masterId) {
      await fetchBillsForMaster(masterId, isType);
    }
  };

  // ── End COD Receipts ──────────────────────────────────────────────────────

  // Filter and search logic
  const filteredData = paymentReceipts.filter(item => {
    const matchesSearch = !searchTerm || 
      item.BillNumber?.toString().toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.MasterName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.Remarks?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' || 
      (filterStatus === 'active' && item.IsActive) ||
      (filterStatus === 'inactive' && !item.IsActive);
    
    const matchesType = filterType === 'all' ||
      (filterType === 'payment' && item.IsType) ||
      (filterType === 'receipt' && !item.IsType);
    
    return matchesSearch && matchesStatus && matchesType;
  });

  // Pagination logic
  const totalPages = Math.ceil(filteredData.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const paginatedData = filteredData.slice(startIndex, startIndex + rowsPerPage);

  const formatCurrency = (amount) => {
    return `₹${(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getPaymentTypeText = (paymentType) => {
    const paymentMethod = paymentMethods.find(pm => pm.Id === paymentType);
    return paymentMethod ? paymentMethod.Name : 'Cash';
  };

  // Handle expand row functionality - REMOVED (not needed)

  return (
    <div style={{ minWidth: '100%', background: '#f5f7fa', padding: '10px', position: 'relative' }}>
      <LogoLoader open={loading || modalLoading} message={loadingMessage} />
      
      <div style={{ maxWidth: '1400px', margin: '0 auto', background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', padding: '12px', position: 'relative', color: 'black', fontFamily: 'Times New Roman' }}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', paddingBottom: '8px', borderBottom: '1px solid #f0f0f0' }}>
          <h4 style={{ fontSize: '17px', fontFamily: 'Times New Roman', fontWeight: '700', margin: 0, textTransform: 'uppercase', color: 'black' }}>Payment & Receipts</h4>
        </div>

        {/* Search, Filters and Create */}
        {isMobile ? (
          <Box sx={{ mb: 1.5 }}>
            <Box sx={{ display: 'flex', gap: '6px', alignItems: 'center', overflowX: 'auto', pb: 0.5, pt: 1, WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' }, overflowY: 'visible' }}>
              <Tooltip title="Search" arrow>
                <IconButton onClick={() => setShowSearch(v => !v)} size="small" sx={{ border: `1px solid ${showSearch ? '#667eea' : '#d1d5db'}`, borderRadius: '8px', height: '32px', width: '32px', flexShrink: 0, bgcolor: showSearch ? '#eff6ff' : 'transparent' }}>
                  <Search sx={{ fontSize: '16px', color: showSearch ? '#667eea' : 'inherit' }} />
                </IconButton>
              </Tooltip>
              <input type="date" lang="en-GB" value={fromDate} min={minDateStr} onChange={(e) => handleFromDateChange(e.target.value)} style={{ height: '32px', border: `1px solid ${dateError ? '#ef4444' : '#d1d5db'}`, borderRadius: '8px', padding: '0 6px', fontSize: '11px', backgroundColor: '#f8f9fa', flexShrink: 0, width: 120 }} />
              <input type="date" lang="en-GB" value={toDate} min={minDateStr} onChange={(e) => handleToDateChange(e.target.value)} style={{ height: '32px', border: `1px solid ${dateError ? '#ef4444' : '#d1d5db'}`, borderRadius: '8px', padding: '0 6px', fontSize: '11px', backgroundColor: '#f8f9fa', flexShrink: 0, width: 120 }} />
              <select value={filterStatus} onChange={(e) => { setFilterStatus(e.target.value); setCurrentPage(1); }} style={{ height: '32px', border: '1px solid #d1d5db', borderRadius: '8px', padding: '0 6px', fontSize: '11px', backgroundColor: '#f8f9fa', flexShrink: 0 }}>
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
              <select value={filterType} onChange={(e) => { setFilterType(e.target.value); setCurrentPage(1); }} style={{ height: '32px', border: '1px solid #d1d5db', borderRadius: '8px', padding: '0 6px', fontSize: '11px', backgroundColor: '#f8f9fa', flexShrink: 0 }}>
                <option value="all">All Types</option>
                <option value="payment">Payment</option>
                <option value="receipt">Receipt</option>
              </select>
              <Tooltip title="Refresh" arrow>
                <IconButton onClick={fetchAllData} disabled={loading} size="small" sx={{ border: '1px solid #d1d5db', borderRadius: '8px', height: '32px', width: '32px', flexShrink: 0 }}>
                  <Refresh sx={{ fontSize: '16px' }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Update Cheque Status" arrow>
                <IconButton onClick={handleOpenBulkChequeStatusModal} size="small" sx={{ background: 'linear-gradient(135deg, #10b981, #059669)', color: 'white', borderRadius: '8px', height: '32px', width: '32px', flexShrink: 0 }}>
                  <Payment sx={{ fontSize: '16px' }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="COD Receipts" arrow>
                <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                  <IconButton onClick={handleOpenCODModal} size="small" sx={{ background: 'linear-gradient(135deg, #f97316, #ea580c)', color: 'white', borderRadius: '8px', height: '32px', width: '32px', flexShrink: 0 }}>
                    <LocalShipping sx={{ fontSize: '16px' }} />
                  </IconButton>
                  {codList.length > 0 && (
                    <Box sx={{
                      position: 'absolute', top: -6, right: -6,
                      background: '#1e293b', color: 'white',
                      borderRadius: '50%', minWidth: '16px', height: '16px',
                      fontSize: '9px', fontWeight: 700,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      px: '3px', lineHeight: 1, pointerEvents: 'none'
                    }}>{codList.length}</Box>
                  )}
                </Box>
              </Tooltip>
              {hasCreatePermission && (
                <Tooltip title="Add Payment/Receipt" arrow>
                  <IconButton onClick={() => handleOpenModal('create')} size="small" sx={{ background: '#2C3E6E', color: 'white', borderRadius: '8px', height: '32px', width: '32px', flexShrink: 0, '&:hover': { background: '#1a2d5a' } }}>
                    <Add sx={{ fontSize: '18px' }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            {showSearch && (
              <Box sx={{ mt: 0.75 }}>
                <TextField autoFocus fullWidth size="small" placeholder="Search by Voucher No, Master..." value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                  InputProps={{ startAdornment: <InputAdornment position="start"><Search style={{ color: '#667eea', fontSize: '18px' }} /></InputAdornment> }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', backgroundColor: '#f8f9fa', fontSize: '13px', height: '32px' } }}
                />
              </Box>
            )}
          </Box>
        ) : (
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap' }}>
            <TextField size="small" placeholder="Search by Voucher No, Master..." value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search style={{ color: '#667eea', fontSize: '20px' }} /></InputAdornment> }}
              sx={{ flex: 1, minWidth: 180, '& .MuiOutlinedInput-root': { borderRadius: '8px', backgroundColor: '#f8f9fa', fontSize: '13px', height: '29px' } }}
            />
            <input type="date" lang="en-GB" value={fromDate} min={minDateStr} onChange={(e) => handleFromDateChange(e.target.value)} style={{ height: '29px', border: `1px solid ${dateError ? '#ef4444' : '#d1d5db'}`, borderRadius: '8px', padding: '0 8px', fontSize: '12px', backgroundColor: '#f8f9fa' }} />
            <input type="date" lang="en-GB" value={toDate} min={minDateStr} onChange={(e) => handleToDateChange(e.target.value)} style={{ height: '29px', border: `1px solid ${dateError ? '#ef4444' : '#d1d5db'}`, borderRadius: '8px', padding: '0 8px', fontSize: '12px', backgroundColor: '#f8f9fa' }} />
            <select value={filterStatus} onChange={(e) => { setFilterStatus(e.target.value); setCurrentPage(1); }} style={{ height: '29px', border: '1px solid #d1d5db', borderRadius: '8px', padding: '0 8px', fontSize: '12px', backgroundColor: '#f8f9fa' }}>
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
            <select value={filterType} onChange={(e) => { setFilterType(e.target.value); setCurrentPage(1); }} style={{ height: '29px', border: '1px solid #d1d5db', borderRadius: '8px', padding: '0 8px', fontSize: '12px', backgroundColor: '#f8f9fa' }}>
              <option value="all">All Types</option>
              <option value="payment">Payment</option>
              <option value="receipt">Receipt</option>
            </select>
            <Tooltip title="Refresh" arrow>
              <IconButton onClick={fetchAllData} disabled={loading} size="small" sx={{ border: '1px solid #d1d5db', borderRadius: '8px', height: '29px', width: '29px' }}>
                <Refresh sx={{ fontSize: '16px' }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Update Cheque Status" arrow>
              <Button variant="contained" onClick={handleOpenBulkChequeStatusModal}
                sx={{ background: 'linear-gradient(135deg, #10b981, #059669)', color: 'white', padding: '4px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: '700', minHeight: '29px', textTransform: 'none', whiteSpace: 'nowrap' }}>
                <Payment sx={{ fontSize: '16px' }} /><span style={{ marginLeft: '4px' }}>Cheque ({filteredData.filter(item => { const id = getChequePaymentMethodId(); return item.PaymentType === id && item.IsActive && (item.ChequeStatusId === null || item.ChequeStatusId === 0 || item.ChequeStatusId === 1); }).length})</span>
              </Button>
            </Tooltip>
            <Tooltip title="COD Receipts" arrow>
              <Button variant="contained" onClick={handleOpenCODModal}
                sx={{ background: 'linear-gradient(135deg, #f97316, #ea580c)', color: 'white', padding: '4px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: '700', minHeight: '29px', textTransform: 'none', whiteSpace: 'nowrap', position: 'relative' }}>
                <LocalShipping sx={{ fontSize: '16px' }} />
                <span style={{ marginLeft: '4px' }}>COD Receipts ({codList.length})</span>
              </Button>
            </Tooltip>
            {hasCreatePermission && (
              <Tooltip title="Add Payment/Receipt" arrow>
                <Button variant="contained" onClick={() => handleOpenModal('create')}
                  sx={{ background: 'linear-gradient(135deg, #2C3E6E 0%, #2C3E6E 100%)', color: 'white', padding: '4px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: '700', minHeight: '29px', textTransform: 'none', whiteSpace: 'nowrap' }}>
                  <Add sx={{ fontSize: '16px' }} /><span style={{ marginLeft: '4px' }}>Add Payment/Receipt</span>
                </Button>
              </Tooltip>
            )}
          </div>
        )}

        {/* Date range error banner */}
        {dateError && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: '8px',
            background: '#fff3cd', border: '1px solid #ffc107',
            borderRadius: '8px', padding: '8px 12px', marginBottom: '10px', color: '#856404'
          }}>
            <Warning style={{ fontSize: '18px', color: '#ffc107', flexShrink: 0 }} />
            <span style={{ fontSize: '12px', fontWeight: '600' }}>{dateError}</span>
          </div>
        )}

        {/* Table */}
        <div style={{ marginBottom: '10px' }}>
          <TableContainer component={Paper} style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.06)', borderRadius: '10px', overflow: 'hidden' }}>
            <Table sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow style={{ background: 'linear-gradient(135deg, #2C3E6E 0%, #2C3E6E 100%)', height: '27px' }}>
                  {['Sl No.', 'Voucher/Receipt No', 'Date', 'Type', 'Master', 'Payment Mode', 'Cheque Status', 'Amount', 'Total', 'Status', 'Actions'].map((h, i) => (
                    <TableCell key={i} style={{ color: 'white', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '4px 12px', height: '27px', textAlign: i >= 7 && i <= 8 ? 'right' : i === 10 ? 'right' : 'left' }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {Array.isArray(paginatedData) && paginatedData.length > 0 && !dateError ? (
                  paginatedData.map((item, index) => (
                    <TableRow key={item.Id || index} style={{ background: index % 2 === 0 ? '#ffffff' : '#f8f9fa', borderBottom: '2px solid #f0f0f0' }}>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748', textAlign: 'center' }}>{startIndex + index + 1}</TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748', fontWeight: 600 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <Payment sx={{ fontSize: 16, color: item.IsType ? '#dc6363' : '#429178' }} />
                          {item.IsType ? 'PMT' : 'RPT'}/{item.BillNumber}{item.FinYear ? `/${item.FinYear}` : ''}
                        </div>
                      </TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>{formatDate(item.BillDate)}</TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px' }}>
                        <Chip label={item.IsType ? 'Payment' : 'Receipt'} size="small"
                          sx={{ backgroundColor: item.IsType ? '#fee2e2' : '#d1fae5', color: item.IsType ? '#dc2626' : '#065f46', fontSize: '0.65rem', height: '20px', fontWeight: 500 }} />
                      </TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>{item.MasterName || '-'}</TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>{getPaymentTypeText(item.PaymentType)}</TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px' }}>
                        {item.PaymentType === getChequePaymentMethodId() ? (
                          <Chip size="small"
                            label={item.ChequeStatusId === 1 ? 'Pending' : item.ChequeStatusId === 2 ? 'Passed' : item.ChequeStatusId === 3 ? 'Bounced' : item.ChequeStatusId === 4 ? 'Cancelled' : 'Unknown'}
                            color={item.ChequeStatusId === 2 ? 'success' : item.ChequeStatusId === 3 ? 'error' : item.ChequeStatusId === 4 ? 'default' : 'warning'}
                            sx={{ fontSize: '0.65rem', height: '20px', fontWeight: 500 }} />
                        ) : '-'}
                      </TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748', fontWeight: 700, textAlign: 'right' }}>{formatCurrency(item.Amount)}</TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748', fontWeight: 700, textAlign: 'right' }}>{formatCurrency(item.TotalAmount)}</TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px' }}>
                        <Chip label={item.IsActive ? 'Active' : 'Inactive'} color={item.IsActive ? 'success' : 'default'} size="small" sx={{ fontSize: '0.65rem', height: '20px' }} />
                      </TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', textAlign: 'right' }}>
                        <Box sx={{ display: 'flex', gap: '4px', justifyContent: 'flex-end' }}>
                          <Tooltip title="View" arrow>
                            <span style={{ cursor: 'pointer', fontSize: '14px' }} onClick={() => handleOpenModal('view', item)}>👁️</span>
                          </Tooltip>
                          <Tooltip title="Print PDF" arrow>
                            <span style={{ cursor: 'pointer', fontSize: '14px' }} onClick={() => handlePrintPaymentReceipt(item.Id)}>🖨️</span>
                          </Tooltip>
                          <Tooltip title="Send via WhatsApp" arrow>
                            <span style={{ cursor: 'pointer', fontSize: '14px' }} onClick={() => handleSendWhatsApp(item.Id)}>
                              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16" style={{ verticalAlign: 'middle' }}>
                                <path fill="#25D366" d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                              </svg>
                            </span>
                          </Tooltip>
                          {item.PaymentType === getChequePaymentMethodId() && item.IsActive && (item.ChequeStatusId === null || item.ChequeStatusId === 0 || item.ChequeStatusId === 1) && (
                            <Tooltip title="Update Cheque Status" arrow>
                              <span style={{ cursor: 'pointer', fontSize: '14px' }} onClick={() => handleOpenChequeStatusModal(item)}>🏦</span>
                            </Tooltip>
                          )}
                          {hasEditPermission && item.IsActive && (
                            <Tooltip title="Edit" arrow>
                              <span style={{ cursor: 'pointer', fontSize: '14px' }} onClick={() => handleOpenModal('edit', item)}>✏️</span>
                            </Tooltip>
                          )}
                          {hasDeletePermission && item.IsActive && (
                            <Tooltip title="Delete" arrow>
                              <span style={{ cursor: 'pointer', fontSize: '14px' }} onClick={() => { setDeleteItemId(item.Id); setDeleteItemNo(item.BillNumber); setDeleteModalOpen(true); }}>🗑️</span>
                            </Tooltip>
                          )}
                          {hasDeletePermission && !item.IsActive && (
                            <Tooltip title="Activate" arrow>
                              <span style={{ cursor: 'pointer', fontSize: '14px' }} onClick={() => { setActivateItemId(item.Id); setActivateItemNo(item.BillNumber); setActivateModalOpen(true); }}>✅</span>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={11} style={{ textAlign: 'center', padding: '20px', color: '#6b7280', fontSize: '13px' }}>
                      {dateError
                        ? <span style={{ color: '#856404', fontWeight: '600' }}>⚠️ {dateError}</span>
                        : 'No payment receipts found'
                      }
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </div>

        {/* Pagination */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '10px', flexWrap: 'wrap', gap: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: '#6b7280' }}>
            <span>Rows per page:</span>
            <select value={rowsPerPage} onChange={(e) => { setRowsPerPage(parseInt(e.target.value)); setCurrentPage(1); }}
              style={{ height: '26px', border: '1px solid #d1d5db', borderRadius: '6px', padding: '0 6px', fontSize: '12px', backgroundColor: '#f8f9fa' }}>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span>{startIndex + 1}–{Math.min(startIndex + rowsPerPage, filteredData.length)} of {filteredData.length}</span>
          </div>
          <div style={{ display: 'flex', gap: '4px' }}>
            {['First', 'Prev'].map((label, i) => (
              <button key={label} onClick={() => setCurrentPage(i === 0 ? 1 : currentPage - 1)} disabled={currentPage === 1}
                style={{ padding: '4px 8px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '11px', background: 'white', cursor: currentPage === 1 ? 'not-allowed' : 'pointer', opacity: currentPage === 1 ? 0.5 : 1 }}>{label}</button>
            ))}
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const page = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
              return (
                <button key={page} onClick={() => setCurrentPage(page)}
                  style={{ padding: '4px 8px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '11px', cursor: 'pointer', background: currentPage === page ? '#2C3E6E' : 'white', color: currentPage === page ? 'white' : '#374151' }}>{page}</button>
              );
            })}
            {['Next', 'Last'].map((label, i) => (
              <button key={label} onClick={() => setCurrentPage(i === 0 ? currentPage + 1 : totalPages)} disabled={currentPage === totalPages}
                style={{ padding: '4px 8px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '11px', background: 'white', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer', opacity: currentPage === totalPages ? 0.5 : 1 }}>{label}</button>
            ))}
          </div>
        </div>

      </div>

      {/* Modal */}
      <PaymentReceiptsModal
        open={openModal}
        onClose={handleCloseModal}
        modalAction={modalAction}
        formData={formData}
        setFormData={setFormData}
        paymentReceiptDetails={paymentReceiptDetails}
        setPaymentReceiptDetails={setPaymentReceiptDetails}
        onSave={handleSave}
        onPrint={handlePrintPaymentReceipt}
        masters={masters}
        banks={banks}
        purchases={purchases}
        sales={sales}
        paymentMethods={paymentMethods}
        gstRates={gstRates}
        loading={modalLoading}
        companyId={companyId}
        token={token}
        fetchPurchases={fetchPurchases}
        fetchSales={fetchSales}
        fetchBillsForMaster={fetchBillsForMaster}
        fetchMaxBillNo={fetchMaxBillNo}
        branchId={branchId}
        fromDate={fromDate}
        toDate={toDate}
      />

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete Payment/Receipt "{deleteItemNo}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteModalOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Activate Confirmation Modal */}
      <Dialog open={activateModalOpen} onClose={() => setActivateModalOpen(false)}>
        <DialogTitle>Confirm Activate</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to activate Payment/Receipt "{activateItemNo}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActivateModalOpen(false)}>Cancel</Button>
          <Button onClick={handleActivate} color="success" variant="contained">
            Activate
          </Button>
        </DialogActions>
      </Dialog>

      {/* Individual Cheque Status Update Modal */}
      <Dialog open={chequeStatusModalOpen} onClose={() => setChequeStatusModalOpen(false)}>
        <DialogTitle>Update Cheque Status</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Update cheque status for Payment/Receipt "{selectedChequeItem?.BillNumber}"
          </Typography>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
              Cheque Number: {selectedChequeItem?.ChequeNumber || 'N/A'}
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
              Amount: {formatCurrency(selectedChequeItem?.Amount || 0)}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Current Status: {
                selectedChequeItem?.ChequeStatusId === 1 ? 'Pending' :
                selectedChequeItem?.ChequeStatusId === 2 ? 'Passed' :
                selectedChequeItem?.ChequeStatusId === 3 ? 'Bounced' :
                selectedChequeItem?.ChequeStatusId === 4 ? 'Cancelled' : 'Unknown'
              }
            </Typography>
          </Box>
          <FormControl fullWidth>
            <InputLabel>New Cheque Status</InputLabel>
            <Select
              value={newChequeStatus}
              onChange={(e) => setNewChequeStatus(e.target.value)}
              label="New Cheque Status"
            >
              <MenuItem value={1}>Pending</MenuItem>
              <MenuItem value={2}>Passed</MenuItem>
              <MenuItem value={3}>Bounced</MenuItem>
              <MenuItem value={4}>Cancelled</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChequeStatusModalOpen(false)}>Cancel</Button>
          <Button onClick={handleUpdateChequeStatus} color="primary" variant="contained">
            Update Status
          </Button>
        </DialogActions>
      </Dialog>

      {/* Enhanced Bulk Cheque Status Update Modal - Compact Version */}
      <Dialog 
        open={bulkChequeStatusModalOpen} 
        onClose={() => setBulkChequeStatusModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            boxShadow: '0 8px 16px rgba(0, 0, 0, 0.1)',
          }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          textAlign: 'center',
          fontSize: '1.2rem',
          fontWeight: 600,
          py: 1.5,
          px: 2
        }}>
          🏦 Cheque Status Manager
        </DialogTitle>
        
        <DialogContent sx={{ p: 0 }}>
          {/* Header Info */}
          <Box sx={{ 
            background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
            p: 1.5,
            borderBottom: '1px solid #e2e8f0'
          }}>
            <Typography variant="subtitle1" sx={{ 
              color: '#1e293b', 
              fontWeight: 600, 
              mb: 0.5,
              fontSize: '0.95rem',
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              📋 Update {selectedChequeItems.length} Cheque Payment{selectedChequeItems.length > 1 ? 's' : ''}
            </Typography>
            <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.8rem' }}>
              Select status for each payment or use quick actions
            </Typography>
          </Box>

          {/* Compact Bulk Actions Bar */}
          <Box sx={{ 
            p: 1.5, 
            background: '#f8fafc',
            borderBottom: '1px solid #e2e8f0',
            display: 'flex',
            gap: 1,
            alignItems: 'center',
            flexWrap: 'wrap'
          }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', fontSize: '0.8rem' }}>
              Quick:
            </Typography>
            {[
              { id: 1, text: 'Pending', color: '#f59e0b', icon: '⏳' },
              { id: 2, text: 'Passed', color: '#10b981', icon: '✅' },
              { id: 3, text: 'Bounced', color: '#ef4444', icon: '❌' },
              { id: 4, text: 'Cancelled', color: '#6b7280', icon: '🚫' }
            ].map((status) => (
              <Button
                key={status.id}
                size="small"
                onClick={() => {
                  const newStatuses = {};
                  selectedChequeItems.forEach(item => {
                    newStatuses[item.Id] = status.id;
                  });
                  setIndividualChequeStatuses(newStatuses);
                }}
                sx={{
                  background: `${status.color}20`,
                  color: status.color,
                  border: `1px solid ${status.color}40`,
                  borderRadius: '6px',
                  fontSize: '0.7rem',
                  fontWeight: 500,
                  textTransform: 'none',
                  minHeight: '24px',
                  px: 1,
                  py: 0.25,
                  '&:hover': {
                    background: `${status.color}30`,
                  }
                }}
              >
                {status.icon} {status.text}
              </Button>
            ))}
          </Box>

          {/* Compact Individual Items List */}
          <Box sx={{ maxHeight: 300, overflow: 'auto', p: 1.5 }}>
            {selectedChequeItems.map((item, index) => {
              const currentStatus = individualChequeStatuses[item.Id] || item.ChequeStatusId || 1;
              const statusInfo = getStatusInfo(currentStatus);
              
              return (
                <Box 
                  key={item.Id} 
                  sx={{ 
                    mb: 1.5,
                    p: 1.5,
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    background: 'white',
                    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                      borderColor: '#3b82f6'
                    }
                  }}
                >
                  {/* Compact Item Header */}
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'flex-start',
                    mb: 1
                  }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle2" sx={{ 
                        fontWeight: 600, 
                        color: '#1e293b',
                        fontSize: '0.85rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        mb: 0.5
                      }}>
                        🧾 {item.BillNumber}
                        <Chip 
                          label={item.IsType ? 'Payment' : 'Receipt'}
                          size="small"
                          sx={{
                            background: item.IsType ? '#fee2e2' : '#d1fae5',
                            color: item.IsType ? '#dc6363' : '#065f46',
                            fontSize: '0.65rem',
                            height: '16px'
                          }}
                        />
                      </Typography>
                      
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                        <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.7rem' }}>
                          📅 {formatDate(item.BillDate)}
                        </Typography>
                        {item.ChequeNumber && (
                          <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.7rem' }}>
                            🏦 {item.ChequeNumber}
                          </Typography>
                        )}
                        <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.7rem' }}>
                          👤 {masters.find(m => m.Id === item.MasterId)?.Name || 'Unknown'}
                        </Typography>
                      </Box>
                    </Box>
                    
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="subtitle2" sx={{ 
                        fontWeight: 700, 
                        color: '#1e293b',
                        fontSize: '0.9rem'
                      }}>
                        {formatCurrency(item.TotalAmount || item.Amount)}
                      </Typography>
                      <Chip 
                        label={`${statusInfo.icon} ${statusInfo.text}`}
                        size="small"
                        sx={{
                          background: statusInfo.bgColor,
                          color: statusInfo.color,
                          fontWeight: 600,
                          fontSize: '0.65rem',
                          height: '18px'
                        }}
                      />
                    </Box>
                  </Box>

                  {/* Compact Status Selector with Individual Update - Same Line */}
                  <Box sx={{ 
                    background: '#f8fafc',
                    borderRadius: '6px',
                    p: 1,
                    border: '1px solid #e2e8f0'
                  }}>
                    <Typography variant="caption" sx={{ 
                      fontWeight: 600, 
                      color: '#374151', 
                      mb: 0.5,
                      fontSize: '0.75rem',
                      display: 'block'
                    }}>
                      🎯 Select Status:
                    </Typography>
                    
                    {/* Status buttons and Update button in same line */}
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      gap: 1,
                      flexWrap: 'wrap'
                    }}>
                      {/* Status Selection Buttons - Left Side */}
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {[
                          { id: 1, text: 'Pending', color: '#f59e0b', bgColor: '#fef3c7', icon: '⏳' },
                          { id: 2, text: 'Passed', color: '#10b981', bgColor: '#d1fae5', icon: '✅' },
                          { id: 3, text: 'Bounced', color: '#ef4444', bgColor: '#fee2e2', icon: '❌' },
                          { id: 4, text: 'Cancelled', color: '#6b7280', bgColor: '#f3f4f6', icon: '🚫' }
                        ].map((status) => (
                          <Button
                            key={status.id}
                            size="small"
                            variant={currentStatus === status.id ? 'contained' : 'outlined'}
                            onClick={() => handleIndividualStatusChange(item.Id, status.id)}
                            sx={{
                              background: currentStatus === status.id ? status.color : 'transparent',
                              color: currentStatus === status.id ? 'white' : status.color,
                              border: `1px solid ${status.color}`,
                              borderRadius: '4px',
                              fontSize: '0.65rem',
                              fontWeight: 500,
                              textTransform: 'none',
                              minWidth: '70px',
                              minHeight: '24px',
                              px: 0.5,
                              py: 0.25,
                              '&:hover': {
                                background: currentStatus === status.id ? status.color : `${status.color}10`,
                              }
                            }}
                          >
                            {status.icon} {status.text}
                          </Button>
                        ))}
                      </Box>

                      {/* Individual Update Button - Right Side */}
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => handleIndividualChequeStatusUpdate(item, currentStatus)}
                        disabled={
                          currentStatus === (item.ChequeStatusId || 1) || 
                          (item.ChequeStatusId !== null && item.ChequeStatusId !== 0 && item.ChequeStatusId !== 1)
                        }
                        sx={{
                          background: 'linear-gradient(135deg, #059669, #047857)',
                          color: 'white',
                          borderRadius: '4px',
                          fontSize: '0.7rem',
                          fontWeight: 600,
                          textTransform: 'none',
                          minHeight: '26px',
                          px: 1.5,
                          boxShadow: '0 1px 3px rgba(5, 150, 105, 0.3)',
                          flexShrink: 0, // Prevent shrinking
                          '&:hover': {
                            background: 'linear-gradient(135deg, #047857, #065f46)',
                            boxShadow: '0 2px 4px rgba(5, 150, 105, 0.4)'
                          },
                          '&:disabled': {
                            background: '#e5e7eb',
                            color: '#9ca3af',
                            boxShadow: 'none'
                          }
                        }}
                      >
                        💾 Update This Item
                      </Button>
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ 
          p: 1.5, 
          background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
          borderTop: '1px solid #e2e8f0',
          gap: 1,
          flexDirection: 'column'
        }}>
          {/* Bulk Actions Info */}
          <Box sx={{ 
            width: '100%', 
            textAlign: 'center', 
            mb: 1,
            p: 1,
            background: '#e0f2fe',
            borderRadius: '6px',
            border: '1px solid #0284c7'
          }}>
            <Typography variant="caption" sx={{ 
              color: '#0284c7', 
              fontWeight: 600,
              fontSize: '0.75rem',
              display: 'block'
            }}>
              💡 Tip: Use individual "Update This Item" buttons above, or use "Update All" below for bulk changes
            </Typography>
          </Box>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 1, width: '100%', justifyContent: 'center' }}>
            <Button 
              onClick={() => {
                setBulkChequeStatusModalOpen(false);
                setIndividualChequeStatuses({});
              }}
              sx={{
                color: '#6b7280',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                textTransform: 'none',
                fontWeight: 500,
                px: 2,
                py: 0.5,
                fontSize: '0.8rem',
                '&:hover': {
                  background: '#f3f4f6',
                  borderColor: '#9ca3af'
                }
              }}
            >
              ❌ Cancel
            </Button>
            
            <Button 
              onClick={handleBulkUpdateChequeStatus} 
              variant="contained"
              disabled={selectedChequeItems.length === 0}
              sx={{
                background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
                borderRadius: '6px',
                textTransform: 'none',
                fontWeight: 600,
                px: 2.5,
                py: 0.5,
                fontSize: '0.8rem',
                boxShadow: '0 2px 4px rgba(59, 130, 246, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #2563eb, #1e40af)',
                  boxShadow: '0 4px 6px rgba(59, 130, 246, 0.4)'
                },
                '&:disabled': {
                  background: '#e5e7eb',
                  color: '#9ca3af'
                }
              }}
            >
              🔄 Update All ({selectedChequeItems.length}) with Selected Status
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* COD Receipts Modal */}
      <Dialog
        open={codModalOpen}
        onClose={() => setCodModalOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px', boxShadow: '0 8px 24px rgba(0,0,0,0.12)' } }}
      >
        <DialogTitle sx={{
          background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
          color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          py: 1.5, px: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocalShipping sx={{ fontSize: '20px' }} />
            <Typography sx={{ fontWeight: 700, fontSize: '1rem' }}>COD Receipts</Typography>
            <Chip label={`${codList.filter(r => !r.IsReceived).length} Pending`} size="small"
              sx={{ bgcolor: 'rgba(255,255,255,0.25)', color: 'white', fontWeight: 700, fontSize: '0.7rem', height: '20px' }} />
          </Box>
          <IconButton onClick={() => setCodModalOpen(false)} size="small" sx={{ color: 'white' }}>
            <Close sx={{ fontSize: '18px' }} />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          {/* Search bar */}
          <Box sx={{ p: 1.5, borderBottom: '1px solid #f1f5f9', background: '#fafafa' }}>
            <TextField
              fullWidth size="small"
              placeholder="Search by customer, bill no, operator..."
              value={codSearch}
              onChange={(e) => setCodSearch(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search style={{ color: '#f97316', fontSize: '18px' }} /></InputAdornment> }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', backgroundColor: '#fff', fontSize: '13px', height: '32px' } }}
            />
          </Box>

          {codLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
              <Typography sx={{ color: '#94a3b8', fontSize: '0.875rem' }}>Loading COD list...</Typography>
            </Box>
          ) : (
            <TableContainer sx={{ maxHeight: 420 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ background: 'linear-gradient(135deg, #2C3E6E 0%, #2C3E6E 100%)' }}>
                    {['#', 'Bill No', 'Customer', 'Operator', 'Bill Total', 'To Collect', 'Payment Mode', 'Status', 'Action'].map((h, i) => (
                      <TableCell key={i} sx={{
                        color: 'white', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase',
                        letterSpacing: '0.4px', py: '6px', px: '10px', bgcolor: '#2C3E6E',
                        textAlign: (i === 4 || i === 5) ? 'right' : 'left'
                      }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(() => {
                    const q = codSearch.trim().toLowerCase();
                    const filtered = q
                      ? codList.filter(r =>
                          (r.CustomerName || '').toLowerCase().includes(q) ||
                          (r.BillNo || '').toLowerCase().includes(q) ||
                          (r.MasterName || '').toLowerCase().includes(q) ||
                          (r.CustomerContactNumber || '').includes(q)
                        )
                      : codList;

                    if (filtered.length === 0) {
                      return (
                        <TableRow>
                          <TableCell colSpan={9} sx={{ textAlign: 'center', py: 5, color: '#94a3b8', fontSize: '0.85rem' }}>
                            No COD records found
                          </TableCell>
                        </TableRow>
                      );
                    }

                    return filtered.map((row, idx) => {
                      const status = row.IsReceived
                        ? 'Collected'
                        : row.AllocatedDate
                          ? 'Processing'
                          : 'Pending';
                      const statusColor = status === 'Collected' ? '#16a34a' : status === 'Processing' ? '#1e40af' : '#ca8a04';
                      const statusBg   = status === 'Collected' ? '#dcfce7' : status === 'Processing' ? '#dbeafe' : '#fef9c3';
                      const canCreate = !row.IsVerified; // allow create if not yet collected

                      return (
                        <TableRow key={row.Id ?? idx}
                          sx={{ background: idx % 2 === 0 ? '#fff' : '#f8f9fa', '&:hover': { background: '#fff7ed' } }}>
                          <TableCell sx={{ fontSize: '11px', py: '6px', px: '10px', color: '#6b7280' }}>{idx + 1}</TableCell>
                          <TableCell sx={{ fontSize: '12px', py: '6px', px: '10px', fontWeight: 700, color: '#f97316', whiteSpace: 'nowrap' }}>
                            {row.BillNo || '-'}
                          </TableCell>
                          <TableCell sx={{ fontSize: '12px', py: '6px', px: '10px' }}>
                            <Typography sx={{ fontWeight: 600, fontSize: '12px', color: '#1e293b', lineHeight: 1.2 }}>
                              {row.CustomerName || '-'}
                            </Typography>
                            {row.CustomerContactNumber && (
                              <Typography sx={{ fontSize: '10px', color: '#64748b' }}>{row.CustomerContactNumber}</Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ fontSize: '12px', py: '6px', px: '10px', color: '#475569' }}>
                            {row.MasterName || '-'}
                          </TableCell>
                          <TableCell sx={{ fontSize: '12px', py: '6px', px: '10px', textAlign: 'right', fontWeight: 600, color: '#1e293b' }}>
                            ₹{(parseFloat(row.BillTotal) || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                          </TableCell>
                          <TableCell sx={{ fontSize: '12px', py: '6px', px: '10px', textAlign: 'right', fontWeight: 700, color: row.IsReceived ? '#16a34a' : '#dc2626' }}>
                            ₹{(parseFloat(row.ToCollect) || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                          </TableCell>
                          <TableCell sx={{ fontSize: '11px', py: '6px', px: '10px', color: '#475569' }}>
                            {row.PaymentMode || 'Cash'}
                          </TableCell>
                          <TableCell sx={{ fontSize: '11px', py: '6px', px: '10px' }}>
                            <Chip label={status} size="small"
                              sx={{ bgcolor: statusBg, color: statusColor, fontWeight: 700, fontSize: '0.62rem', height: '18px' }} />
                          </TableCell>
                          <TableCell sx={{ py: '6px', px: '10px' }}>
                            {canCreate ? (
                              <Tooltip title="Verify & create receipt for this COD" arrow>
                                <Button size="small" variant="contained"
                                  onClick={() => handleCODReceiptCreate(row)}
                                  sx={{
                                    background: 'linear-gradient(135deg, #16a34a, #15803d)',
                                    color: 'white', fontSize: '10px', fontWeight: 700,
                                    textTransform: 'none', borderRadius: '5px',
                                    minWidth: '60px', minHeight: '22px', px: 1, py: 0.25,
                                    '&:hover': { background: 'linear-gradient(135deg, #15803d, #166534)' }
                                  }}>
                                  ✔ Verify
                                </Button>
                              </Tooltip>
                            ) : (
                              <Chip label="Done" size="small"
                                sx={{ bgcolor: '#dcfce7', color: '#16a34a', fontWeight: 700, fontSize: '0.6rem', height: '18px' }} />
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    });
                  })()}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 2, py: 1.25, background: '#f8fafc', borderTop: '1px solid #e2e8f0', justifyContent: 'space-between' }}>
          <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.75rem' }}>
            Click "✔ Verify" to create a receipt pre-filled with the COD customer's sale data
          </Typography>
          <Button onClick={() => setCodModalOpen(false)} size="small"
            sx={{ color: '#6b7280', border: '1px solid #d1d5db', borderRadius: '6px', textTransform: 'none', fontSize: '0.8rem', px: 2 }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error Snackbars */}
      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess('')}
      >
        <Alert onClose={() => setSuccess('')} severity="success">
          {success}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError('')}
      >
        <Alert onClose={() => setError('')} severity="error">
          {error}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default PaymentReceiptsIndex;
